import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy } from "@dnd-kit/sortable";
import { useAuth } from "@/context/AuthContext";
import { api, inr, formatError } from "@/lib/api";
import {
  CheckCircle2,
  CreditCard,
  Download,
  Eye,
  EyeOff,
  Facebook,
  FileText,
  Gift,
  MessageSquareText,
  Globe,
  Image,
  Instagram,
  Layers,
  Loader2,
  LogOut,
  Mail,
  MapPin,
  MousePointerClick,
  Package,
  Phone,
  Pencil,
  Plug,
  Plus,
  Power,
  Printer,
  RotateCcw,
  Save,
  Send,
  Settings,
  ShoppingBag,
  Sparkles,
  Ticket,
  Trash2,
  Truck,
  Upload as UploadIcon,
  Users,
  XCircle,
  Youtube,
} from "lucide-react";
import { toast } from "sonner";
import MediaPicker from "@/components/MediaPicker";
import { AdminDashboard } from "@/components/admin/AdminDashboard";
import { FeedbackReviewsTab } from "@/components/admin/FeedbackReviewsTab";
import { AdminProfileTab } from "@/components/admin/AdminProfileTab";
import { EmailDeliveryMonitor } from "@/components/admin/EmailDeliveryMonitor";
import { EiraPointsAdminTab } from "@/components/admin/EiraPointsAdminTab";
import { EmailAutomationSettings } from "@/components/admin/EmailAutomationSettings";
import { CopyCheckPanel } from "@/components/admin/CopyCheckPanel";
import { SupportTicketsTab } from "@/components/admin/SupportTicketsTab";
import { CorporateEnquiriesTab } from "@/components/admin/CorporateEnquiriesTab";
import { PurchaseManagementTab } from "@/components/admin/PurchaseManagementTab";
import { SortableCategoryCard } from "@/components/admin/SortableCategoryCard";
import { LogoutConfirmDialog } from "@/components/LogoutConfirmDialog";
import { formatIndianPhone, PhoneNumberInput } from "@/components/PhoneNumberInput";
import { useBranding } from "@/context/BrandingContext";
import { applyTrackingSettings } from "@/lib/analytics";
import {
  AdminDeleteConfirmProvider,
  useAdminDeleteConfirmation,
} from "@/context/AdminDeleteConfirmContext";
import { getAdminUrl, getMainLoginUrl } from "@/lib/host";
import { HamperConfig } from "@/components/admin/HamperConfig";
import { AdminSidebar, ADMIN_TABS } from "@/components/admin/AdminSidebar";
import { ReturnsTab } from "@/components/admin/ReturnsTab";
import { AccountDeletionRequestsTab } from "@/components/admin/AccountDeletionRequestsTab";
import { OrderFulfillmentDetails } from "@/components/admin/OrderFulfillmentDetails";
import { OrderPaymentRefundDetails } from "@/components/admin/OrderPaymentRefundDetails";
import {
  clearAdminValidationError,
  handleAdminNativeInvalid,
  showAdminFieldErrors,
} from "@/components/admin/adminValidation";

const getAdminTabFromUrl = () => {
  const tab = new URLSearchParams(window.location.search).get("tab");
  return ADMIN_TABS.some((item) => item.k === tab) ? tab : "dash";
};


const Field = ({ label, hint, children, span = "" }) => (
  <div className={span}>
    <label className="text-xs font-semibold uppercase tracking-wider text-[#8C6D46] block mb-1">{label}</label>
    {children}
    {hint && <div className="text-[11px] text-[#8C6D46] mt-1">{hint}</div>}
  </div>
);

const VARIANT_PRESETS = [
  "100g",
  "250g",
  "500g",
  "1kg",
  "100ml",
  "200ml",
  "500ml",
  "1L",
  "S",
  "M",
  "L",
  "XL",
];

const ProductsTab = () => {
  const { requestDelete } = useAdminDeleteConfirmation();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const emptyProduct = {
    product_type: "standard",
    hamper_required_count: 2,
    hamper_options: [],
    hamper_custom_items: [],
    name: "",
    price: 0,
    mrp: 0,
    category_id: "",
    description: "",
    hsn_code: "",
    color_code: "",
    food_symbol: "not_applicable",
    return_policy: "no_return",
    features: [],
    specifications: {},
    how_to_use: "",
    images: [],
    video_url: null,
    stock: 20,
    box_stock: 0,
    estimated_duration_days: "",
    variants: [],
    materials: [],
    publish_at: "",
    active: true,
  };
  const [f, setF] = useState(emptyProduct);
  const [mediaBusy, setMediaBusy] = useState(false);
  const [productSort, setProductSort] = useState({ key: "name", direction: "asc" });
  const [editingId, setEditingId] = useState(null);
  const load = () => api.get("/admin/products?include_scheduled=1").then((r) => setItems(r.data.items || r.data));
  useEffect(() => { load(); api.get("/admin/categories").then((r) => setCats(r.data)); api.get("/admin/brands").then((r) => setBrands(r.data)); }, []);
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, [editingId]);
  const resetForm = () => {
    window.scrollTo({ top: 0, left: 0 });
    setEditingId(null);
    setF(emptyProduct);
  };
  const save = async () => {
    const missingRequiredFields = [
      !f.name.trim() && "new-prod-name",
      !f.category_id && "new-prod-category",
      !f.hsn_code?.trim() && "product-hsn-code",
    ].filter(Boolean);
    if (missingRequiredFields.length) {
      showAdminFieldErrors("Name, category, and HSN Code are required", missingRequiredFields);
      return;
    }
    if (!f.images || f.images.length === 0) {
      showAdminFieldErrors("Upload at least one photo", ["new-prod-media"]);
      return;
    }
    const variants = (f.variants || []).map((variant) => ({
      ...variant,
      label: variant.label?.trim() || "",
    }));
    const isHamper = f.product_type === "gift_box";
    const selectableHamperItems = (
      (f.hamper_options?.length || 0) + (f.hamper_custom_items?.length || 0)
    );
    if (
      isHamper
      && (
        !Number.isInteger(f.hamper_required_count)
        || f.hamper_required_count < 1
        || !selectableHamperItems
      )
    ) {
      showAdminFieldErrors("Set the required count and add at least one box item", [
        "admin-hamper-required-count",
        "admin-hamper-config",
      ]);
      return;
    }
    if (isHamper && (Number.isNaN(Number(f.price)) || Number(f.price) < 0)) {
      showAdminFieldErrors("Enter a valid base price for the gift box", [
        "admin-hamper-base-price",
      ]);
      return;
    }
    if (isHamper && (!Number.isInteger(Number(f.box_stock)) || Number(f.box_stock) < 0)) {
      showAdminFieldErrors("Enter a valid box-level stock quantity", [
        "admin-hamper-box-stock",
      ]);
      return;
    }
    if (!isHamper && (!variants.length || variants.some((variant) => !variant.label))) {
      const missingVariantLabels = variants
        .map((variant, index) => !variant.label && `variant-label-${index}`)
        .filter(Boolean);
      showAdminFieldErrors("Add at least one named size or variant before saving", [
        ...(missingVariantLabels.length ? missingVariantLabels : ["product-variants-section"]),
      ]);
      return;
    }
    if (!isHamper && variants.some((variant) => (
      variant.price === ""
      || variant.price === null
      || variant.price === undefined
      || Number.isNaN(Number(variant.price))
    ))) {
      const missingVariantPrices = variants
        .map((variant, index) => (
          (
            variant.price === ""
            || variant.price === null
            || variant.price === undefined
            || Number.isNaN(Number(variant.price))
          ) && `variant-price-${index}`
        ))
        .filter(Boolean);
      showAdminFieldErrors("Enter a price for every variant", missingVariantPrices);
      return;
    }
    try {
      const payload = {
        ...f,
        slug: f.slug || f.name.toLowerCase().replace(/\s+/g, "-"),
        description: f.description || f.name,
        short_description: f.short_description || f.description || f.name,
        features: f.features || [],
        specifications: f.specifications || {},
        variants: isHamper ? [] : variants,
        hamper_required_count: isHamper ? f.hamper_required_count : null,
        hamper_options: isHamper ? f.hamper_options : [],
        hamper_custom_items: isHamper ? f.hamper_custom_items : [],
        price: isHamper ? Number(f.price) : Number(variants[0].price),
        mrp: isHamper ? Number(f.price) : Number(variants[0].mrp ?? variants[0].price),
        stock: isHamper ? 0 : variants[0].stock ?? f.stock,
        box_stock: isHamper ? Number(f.box_stock) : null,
        tags: f.tags || [],
        hsn_code: f.hsn_code.trim(),
        publish_at: f.publish_at ? new Date(f.publish_at).toISOString() : null,
      };
      await (editingId ? api.put(`/products/${editingId}`, payload) : api.post("/products", payload));
      toast.success(editingId ? "Product updated" : payload.publish_at && new Date(payload.publish_at) > new Date() ? "Product scheduled 🕒" : "Product published");
      resetForm();
      load();
    } catch (e) { toast.error(formatError(e)); }
  };
  const edit = (product) => {
    setTimeout(() => {
      document.getElementById("admin-product-editor")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 0);
    const { id, created_at, click_count, ...productFields } = product;
    const boxStock = product.product_type === "gift_box"
      ? product.box_stock ?? product.stock ?? 0
      : null;
    setEditingId(id);
    setF({
      ...emptyProduct,
      ...productFields,
      box_stock: boxStock,
      publish_at: product.publish_at ? product.publish_at.slice(0, 16) : "",
    });
  };
  const toggleActive = async (product) => {
    await api.put(`/products/${product.id}/active`, { active: !product.active });
    load();
  };
  const del = (id) => requestDelete(async () => {
    await api.delete(`/products/${id}`);
    await load();
  });
  const resetClicks = async (id, name) => {
    if (!confirm(`Reset click count to 0 for "${name}"?`)) return;
    try { await api.post(`/admin/products/${id}/reset-clicks`); toast.success("Click count reset"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };
  const addVariant = (label = "") => {
    const existing = (f.variants || []).some(
      (variant) => variant.label?.trim().toLowerCase() === label.toLowerCase(),
    );
    if (label && existing) {
      toast.error("That variant is already added");
      return;
    }
    setF({
      ...f,
      variants: [
        ...(f.variants || []),
        { label, material: "", price: "", mrp: null, stock: null },
      ],
    });
  };
  const updateVariant = (index, patch) => {
    const variants = (f.variants || []).map(
      (variant, variantIndex) => (
        variantIndex === index ? { ...variant, ...patch } : variant
      ),
    );
    const primaryVariant = variants[0];
    setF({
      ...f,
      variants,
      price: primaryVariant?.price ?? f.price,
      mrp: primaryVariant?.mrp ?? primaryVariant?.price ?? f.mrp,
      stock: primaryVariant?.stock ?? f.stock,
    });
  };
  const removeVariant = (index) => {
    setF({ ...f, variants: (f.variants || []).filter((_, variantIndex) => variantIndex !== index) });
  };
  const toggleProductSort = (key) => {
    setProductSort((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };
  const sortedProducts = [...items].sort((left, right) => {
    const getValue = (product) => {
      if (productSort.key === "media") return product.images?.length || 0;
      if (productSort.key === "clicks") return product.click_count || 0;
      if (productSort.key === "stock") return Number(product.variants?.[0]?.stock ?? product.stock ?? 0);
      if (productSort.key === "status") return product.active ? 1 : 0;
      return productSort.key === "name" ? product.name || "" : Number(product[productSort.key] || 0);
    };
    const leftValue = getValue(left);
    const rightValue = getValue(right);
    const comparison = typeof leftValue === "string" ? leftValue.localeCompare(rightValue) : leftValue - rightValue;
    return productSort.direction === "asc" ? comparison : -comparison;
  });
  const ProductSortHeader = ({ label, sortKey }) => (
    <th className="p-3">
      <button
        type="button"
        onClick={() => toggleProductSort(sortKey)}
        className="inline-flex items-center gap-1 font-semibold"
        data-testid={`sort-product-${sortKey}`}
      >
        {label}
        <span>
          {productSort.key === sortKey ? productSort.direction === "asc" ? "↑" : "↓" : "↕"}
        </span>
      </button>
    </th>
  );
  return (
    <div>
      <h2 className="font-serif text-3xl font-semibold mb-6">Products ({items.length})</h2>
      <div id="admin-product-editor" className="bg-white border border-[#E2DCD2] rounded-xl p-5 mb-6" data-testid="admin-product-editor">
        <h3 className="font-semibold mb-4 text-lg">{editingId ? "Edit Product" : "Add Product"}</h3>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
          <Field label="Product name" span="md:col-span-2">
            <input placeholder="e.g. Handcrafted Brass Cooking Pot" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="new-prod-name" />
          </Field>
          <Field label="HSN Code" hint="Required for invoices">
            <input
              placeholder="e.g. 21069099"
              value={f.hsn_code}
              onChange={(e) => setF({ ...f, hsn_code: e.target.value })}
              className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm"
              data-testid="product-hsn-code"
            />
          </Field>
          <Field label="Category">
            <select value={f.category_id} onChange={(e) => setF({ ...f, category_id: e.target.value })} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="new-prod-category">
              <option value="">— Select —</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Brand">
            <select value={f.brand || ""} onChange={(e) => setF({ ...f, brand: e.target.value })} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="product-brand">
              <option value="">— Select —</option>{brands.map((brand) => <option key={brand.id} value={brand.name}>{brand.name}</option>)}
            </select>
          </Field>
          <Field label="Food Symbol">
            <select value={f.food_symbol || "not_applicable"} onChange={(e) => setF({ ...f, food_symbol: e.target.value })} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="product-food-symbol"><option value="not_applicable">Not Applicable</option><option value="veg">🟢 Veg</option><option value="non_veg">🔴 Non-Veg</option><option value="vegan">🌿 Vegan</option></select>
          </Field>
          <Field label="Return Policy">
            <select value={f.return_policy || "no_return"} onChange={(e) => setF({ ...f, return_policy: e.target.value })} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="product-return-policy"><option value="no_return">No Return</option><option value="7_day">7 Day Return</option><option value="15_day">15 Day Return</option><option value="30_day">30 Day Return</option></select>
          </Field>
          <Field
            label="Estimated Duration (days)"
            hint="Optional · starts from delivery"
          >
            <input
              type="number"
              min="1"
              max="3650"
              step="1"
              value={f.estimated_duration_days ?? ""}
              onChange={(event) => setF({
                ...f,
                estimated_duration_days: event.target.value === ""
                  ? null
                  : Number(event.target.value),
              })}
              placeholder="e.g. 30"
              className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm"
              data-testid="product-estimated-duration-days"
            />
          </Field>
        </div>
        <div className="mb-4 max-w-sm">
          <Field label="Product type"><select value={f.product_type} onChange={(event) => setF({ ...f, product_type: event.target.value, hamper_required_count: f.hamper_required_count || 2 })} className="w-full rounded-lg border border-[#E2DCD2] px-3 py-2 text-sm" data-testid="admin-product-type"><option value="standard">Regular Product</option><option value="gift_box">Gift Box / Hamper</option></select></Field>
        </div>
        {f.product_type === "gift_box" ? <HamperConfig value={f} onChange={setF} products={items} editingId={editingId} /> : <div className="mb-4 border border-[#E2DCD2] rounded-xl p-4" data-testid="product-variants-section">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h4 className="font-semibold text-[#1E2A24]">Variants / Sizes</h4>
              <p className="mt-1 text-xs text-[#64748B]">
                Add at least one real option. Each variant controls its own selling price and MRP.
              </p>
            </div>
            <button
              type="button"
              onClick={() => addVariant()}
              className="inline-flex items-center gap-1 rounded border border-[#2D5A47] px-3 py-2 text-xs font-semibold text-[#2D5A47] hover:bg-[#EBF2EE]"
              data-testid="add-custom-variant"
            >
              <Plus size={13} /> Add custom variant
            </button>
          </div>
          <div className="mt-4 border border-[#D9C3A5] bg-[#FDFBF7] p-3" data-testid="product-material-options">
            <label className="block text-sm font-semibold text-[#1E2A24]">Material choices (optional)</label>
            <p className="mt-1 text-xs text-[#64748B]">Add materials manually, separated by commas. Example: Glass, Plastic.</p>
            <input
              value={(f.materials || []).join(", ")}
              onChange={(event) => setF({
                ...f,
                materials: event.target.value.split(",").map((value) => value.trim()).filter(Boolean),
              })}
              placeholder="Glass, Plastic"
              className="mt-2 w-full border border-[#E2DCD2] bg-white px-3 py-2 text-sm"
              data-testid="product-material-options-input"
            />
          </div>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="variant-preset-options">
            {VARIANT_PRESETS.map((label) => (
              <button
                key={label}
                type="button"
                onClick={() => addVariant(label)}
                className="rounded border border-[#E2DCD2] bg-[#FDFBF7] px-2.5 py-1.5 text-xs font-semibold text-[#64748B] hover:border-[#2D5A47] hover:text-[#2D5A47]"
                data-testid={`add-variant-preset-${label}`}
              >
                + {label}
              </button>
            ))}
          </div>
          {(f.variants || []).length > 0 ? (
            <div className="mt-4 space-y-3">
              {(f.variants || []).map((variant, index) => (
                <div
                  key={`${variant.label || "manual"}-${index}`}
                  className="grid grid-cols-1 items-start gap-3 border border-[#E2DCD2] bg-[#FDFBF7] p-3 md:grid-cols-2 xl:grid-cols-[minmax(0,1.5fr)_repeat(4,minmax(0,1fr))_2.5rem] [&>div]:min-w-0 md:[&>div>label]:flex md:[&>div>label]:min-h-8 md:[&>div>label]:items-end"
                  data-testid={`product-variant-row-${index}`}
                >
                  <Field label="Size / Weight / Volume">
                    <input
                      value={variant.label || ""}
                      onChange={(event) => updateVariant(index, { label: event.target.value })}
                      placeholder="e.g. 250g, 1kg, Large"
                      className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm"
                      data-testid={`variant-label-${index}`}
                    />
                  </Field>
                  <Field label="Material" hint="Optional">
                    <input
                      value={variant.material || ""}
                      onChange={(event) => updateVariant(index, { material: event.target.value })}
                      placeholder="e.g. Glass, Plastic"
                      className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm"
                      data-testid={`variant-material-${index}`}
                    />
                  </Field>
                  <Field label="Variant price (₹)">
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={variant.price ?? ""}
                      onChange={(event) => updateVariant(index, {
                        price: event.target.value === "" ? "" : Number(event.target.value),
                      })}
                      className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm"
                      data-testid={`variant-price-${index}`}
                    />
                  </Field>
                  <Field label="Variant MRP (₹)" hint="Optional">
                    <input
                      type="number"
                      min="0"
                      value={variant.mrp ?? ""}
                      onChange={(event) => updateVariant(index, {
                        mrp: event.target.value === "" ? null : Number(event.target.value),
                      })}
                      placeholder="Same as price"
                      className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm"
                      data-testid={`variant-mrp-${index}`}
                    />
                  </Field>
                  <Field label="Variant stock" hint="Controls this variant's availability">
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={variant.stock ?? ""}
                      onChange={(event) => updateVariant(index, {
                        stock: event.target.value === "" ? null : Number(event.target.value),
                      })}
                      placeholder="Uses product stock"
                      className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm"
                      data-testid={`variant-stock-${index}`}
                    />
                  </Field>
                  <button
                    type="button"
                    onClick={() => removeVariant(index)}
                    className="flex h-[38px] w-10 items-center justify-center justify-self-end rounded text-red-500 transition-colors hover:bg-red-50 md:mt-9 md:justify-self-start"
                    aria-label={`Remove variant ${index + 1}`}
                    title="Remove variant"
                    data-testid={`remove-variant-${index}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-4 text-xs text-[#8C6D46]" data-testid="variants-required-note">
              Add a named size or variant before saving. Products do not receive a Default variant.
            </p>
          )}
        </div>}
        <div className="mb-4">
          <Field label="Description" hint="Shown on the product detail page">
            <textarea
              value={f.description}
              onChange={(e) => setF({ ...f, description: e.target.value })}
              rows={3}
              className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm"
              data-testid="product-description"
            />
          </Field>
        </div>
        <div className="grid gap-4 md:grid-cols-2 mb-4">
          <Field label="Key Features" hint="One point per line">
            <textarea value={(f.features || []).join("\n")} onChange={(e) => setF({ ...f, features: e.target.value.split("\n").filter(Boolean) })} rows={4} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="product-features" />
          </Field>
          <Field label="Specifications" hint="One Key: Value pair per line">
            <textarea value={Object.entries(f.specifications || {}).map(([key, value]) => `${key}: ${value}`).join("\n")} onChange={(e) => setF({ ...f, specifications: Object.fromEntries(e.target.value.split("\n").map((line) => line.split(/:(.*)/)).filter(([key, value]) => key && value).map(([key, value]) => [key.trim(), value.trim()])) })} rows={4} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="product-specifications" />
          </Field>
        </div>
        <div className="mb-4"><Field label="How to Use"><textarea value={f.how_to_use || ""} onChange={(e) => setF({ ...f, how_to_use: e.target.value })} rows={3} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="product-how-to-use" /></Field></div>
        <div className="mb-4 grid md:grid-cols-2 gap-4 items-end">
          <Field label="Schedule publish (optional)" hint="Leave empty to go live immediately">
            <input type="datetime-local" value={f.publish_at} onChange={(e) => setF({ ...f, publish_at: e.target.value })} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="new-prod-publish-at" />
          </Field>
          <div className="text-xs text-[#8C6D46]">
            {f.publish_at ? <>Scheduled to go live on <b>{new Date(f.publish_at).toLocaleString("en-IN")}</b></> : "Product will be visible immediately after publish"}
          </div>
        </div>
        <div className="mb-4">
          <label className="text-xs font-semibold uppercase tracking-wider text-[#8C6D46] block mb-1">Product Media</label>
          <div className="text-[11px] text-[#8C6D46] mb-2">Up to 4 images + 1 video. Recommended: <b>1200 × 1200 px square</b> images for best display.</div>
          <MediaPicker
            images={f.images}
            setImages={(imgs) => setF({ ...f, images: imgs })}
            video={f.video_url}
            setVideo={(v) => setF({ ...f, video_url: v })}
            maxImages={4}
            allowVideo={true}
            imageHint="1200 × 1200 px · 1:1 square · JPG/PNG/WebP · ≤ 8 MB"
            videoHint="1080 × 1080 or 1920 × 1080 · MP4/WebM · ≤ 50 MB · ≤ 30 s"
            onBusyChange={setMediaBusy}
            testId="new-prod-media"
          />
        </div>
        <div className="flex gap-3">
          <button onClick={save} disabled={mediaBusy} className="bg-[#2D5A47] hover:bg-[#214435] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg px-5 py-2.5 text-sm font-semibold flex items-center gap-1" data-testid="save-product">
            <Plus size={14} /> {mediaBusy ? "Uploading media…" : editingId ? "Save Product" : "Publish Product"}
          </button>
          {editingId && <button onClick={resetForm} className="text-sm text-[#64748B]" data-testid="cancel-product-edit">Cancel</button>}
        </div>
      </div>
      <div className="bg-white border border-[#E2DCD2] rounded-xl overflow-hidden">
        <div className="admin-table-scroll" data-testid="admin-products-table-scroll">
          <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-[#F4F1EA] text-left"><tr><ProductSortHeader label="Product" sortKey="name" /><ProductSortHeader label="Price" sortKey="price" /><ProductSortHeader label="Media" sortKey="media" /><ProductSortHeader label="Stock" sortKey="stock" /><ProductSortHeader label="Clicks" sortKey="clicks" /><ProductSortHeader label="Status" sortKey="status" /><th></th></tr></thead>
          <tbody>
            {sortedProducts.map((p) => {
              const scheduled = p.publish_at && new Date(p.publish_at) > new Date();
              const clicks = p.click_count || 0;
              const stock = Number(p.variants?.[0]?.stock ?? p.stock ?? 0);
              return (
              <tr key={p.id} className="border-t border-[#E2DCD2]">
                <td className="p-3 flex items-center gap-2"><img src={p.images?.[0]} className="w-10 h-10 rounded object-cover" /><span>{p.name}</span></td>
                <td>{inr(p.price)}</td>
                <td className="text-xs text-[#64748B]">{p.images?.length || 0} img{p.video_url ? " · 1 video" : ""}</td>
                <td>{p.stock}</td>
                <td>
                  <div className="flex items-center gap-1.5 text-sm font-semibold" data-testid={`admin-clicks-${p.id}`}>
                    <MousePointerClick size={12} className="text-[#C86D42]" />
                    <span className={clicks > 0 ? "text-[#1E2A24]" : "text-[#8C6D46]"}>{clicks}</span>
                  </div>
                </td>
                <td>
                  <div className="flex flex-wrap gap-1" data-testid={`product-status-${p.id}`}>
                    {!p.active ? <span className="border border-red-200 bg-red-50 px-1.5 py-0.5 text-xs font-semibold text-red-700">Not live</span>
                      : scheduled ? <span className="border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-xs font-semibold text-amber-700">Scheduled</span>
                      : <span className="border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-xs font-semibold text-emerald-700">Live</span>}
                    {stock <= 0 ? <span className="border border-red-200 bg-red-50 px-1.5 py-0.5 text-xs font-semibold text-red-700">Out of stock</span>
                      : stock <= 10 ? <span className="border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-xs font-semibold text-amber-700">Low stock · {stock}</span>
                      : null}
                  </div>
                </td>
                <td>
                  <div className="flex items-center gap-1">
                    <button onClick={() => toggleActive(p)} className={`rounded p-2 text-white ${p.active ? "bg-emerald-600" : "bg-[#7F1D1D]"}`} data-testid={`toggle-product-${p.id}`} title={p.active ? "Disable" : "Enable"}><Power size={14} /></button>
                    <button onClick={() => edit(p)} title="Edit" className="text-[#2D5A47] p-2 hover:bg-[#EBF2EE] rounded" data-testid={`edit-product-${p.id}`}><Pencil size={14} /></button>
                    <button onClick={() => resetClicks(p.id, p.name)} disabled={clicks === 0} title="Reset click count" className="text-[#8C6D46] p-2 hover:bg-[#F4F1EA] rounded disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`reset-clicks-${p.id}`}><RotateCcw size={14} /></button>
                    <button onClick={() => del(p.id)} title="Delete" className="text-red-500 p-2 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
          </table>
        </div>
        <div className="border-t border-[#E2DCD2] px-4 py-3 text-sm text-[#64748B]" data-testid="products-footer-count">{sortedProducts.length} product(s) shown</div>
      </div>
    </div>
  );
};

const OrdersTab = ({ dashboardFilter, onClearDashboardFilter }) => {
  const [items, setItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [orderIdFilter, setOrderIdFilter] = useState("");
  const [exporting, setExporting] = useState(false);
  const [retryingRefundId, setRetryingRefundId] = useState(null);
  const [retryingShipmentId, setRetryingShipmentId] = useState(null);
  const [retryingCancellationId, setRetryingCancellationId] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const load = useCallback((range = {}) => {
    const params = Object.fromEntries(
      Object.entries(range).filter(([, value]) => Boolean(value)),
    );
    return api.get("/admin/orders", { params }).then((r) => setItems(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!dashboardFilter) return;
    const nextFilter = {
      start_date: dashboardFilter.start_date || "",
      end_date: dashboardFilter.end_date || "",
      status: dashboardFilter.status || "",
      order_id: dashboardFilter.order_id || "",
    };
    setStartDate(nextFilter.start_date);
    setEndDate(nextFilter.end_date);
    setStatusFilter(nextFilter.status);
    setOrderIdFilter(nextFilter.order_id);
    setAppliedFilters(nextFilter);
    setSelectedIds([]);
    load(nextFilter);
  }, [dashboardFilter, load]);
  const setStatus = async (id, status) => {
    try {
      const { data } = await api.put(`/admin/orders/${id}/status`, { status });
      if (data.already_set) {
        toast.message("Order status is already up to date");
      } else if (status === "cancelled") {
        toast.success("Order cancelled. Shiprocket sync status is shown in the order row.");
      }
      load(appliedFilters);
    } catch (error) {
      toast.error(formatError(error));
      load(appliedFilters);
    }
  };
  const confirmStatusChange = (order, status) => {
    if (status === "cancelled" && !window.confirm("Cancel this order and request Shiprocket cancellation?")) {
      return;
    }
    setStatus(order.id, status);
  };
  const print = (id) => {
    window.location.assign(getAdminUrl(`/admin/print/${id}`));
  };
  const preview = (id) => {
    window.open(getAdminUrl(`/admin/print/${id}?preview=1`), "_blank", "noopener");
  };
  const toggleOrder = (id) => {
    setSelectedIds((currentIds) => {
      if (currentIds.includes(id)) return currentIds.filter((orderId) => orderId !== id);
      if (currentIds.length >= 100) {
        toast.error("You can select up to 100 orders to print.");
        return currentIds;
      }
      return [...currentIds, id];
    });
  };
  const toggleAllOrders = () => {
    const visibleIds = pagedItems.slice(0, 100).map((order) => order.id);
    const allVisibleSelected = visibleIds.every((id) => selectedIds.includes(id));
    setSelectedIds(allVisibleSelected ? [] : visibleIds);
  };
  const printSelected = () => {
    if (!selectedIds.length) return;
    window.location.assign(getAdminUrl(`/admin/print?ids=${selectedIds.join(",")}`));
  };
  const retryRefund = async (order) => {
    setRetryingRefundId(order.id);
    try {
      await api.post(`/admin/orders/${order.id}/refund-retry`);
      toast.success("Refund retry initiated");
      load(appliedFilters);
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setRetryingRefundId(null);
    }
  };
  const retryShipment = async (order) => {
    setRetryingShipmentId(order.id);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/shiprocket/retry`);
      if (data.tracking?.status === "error") {
        toast.error("Shiprocket retry failed. Review the provider response.");
      } else {
        toast.success("Shiprocket push retried");
      }
      load(appliedFilters);
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setRetryingShipmentId(null);
    }
  };
  const retryShiprocketCancellation = async (order) => {
    setRetryingCancellationId(order.id);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/shiprocket/cancel-retry`);
      if (data.cancellation?.state === "synced") {
        toast.success("Shiprocket cancellation synced");
      } else {
        toast.error("Shiprocket cancellation retry failed. Review the provider response.");
      }
      load(appliedFilters);
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setRetryingCancellationId(null);
    }
  };
  const exportOrders = async () => {
    const params = Object.fromEntries(
      Object.entries(appliedFilters).filter(([, value]) => Boolean(value)),
    );
    setExporting(true);
    try {
      const response = await api.get("/admin/orders/export.xlsx", {
        params,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = "eira-orders-export.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Orders exported to Excel");
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setExporting(false);
    }
  };
  const clearDateFilter = () => {
    setStartDate("");
    setEndDate("");
    setStatusFilter("");
    setOrderIdFilter("");
    setAppliedFilters({});
    onClearDashboardFilter();
    setPage(1);
    load({});
  };
  const applyOrderFilters = () => {
    const nextFilters = {
      start_date: startDate,
      end_date: endDate,
      status: statusFilter,
      order_id: orderIdFilter,
    };
    setAppliedFilters(nextFilters);
    setSelectedIds([]);
    setPage(1);
    load(nextFilters);
  };
  const hasAppliedFilters = Object.values(appliedFilters).some(Boolean);
  const filtersAreCurrent = ["start_date", "end_date", "status", "order_id"].every(
    (key) => ({
      start_date: startDate,
      end_date: endDate,
      status: statusFilter,
      order_id: orderIdFilter,
    })[key] === (appliedFilters[key] || ""),
  );
  const activeFilterSummary = [
    appliedFilters.start_date && `From ${appliedFilters.start_date}`,
    appliedFilters.end_date && `to ${appliedFilters.end_date}`,
    appliedFilters.status && `Status: ${appliedFilters.status}`,
    appliedFilters.order_id && `Order: ${appliedFilters.order_id.slice(0, 8)}`,
  ].filter(Boolean).join(" · ");
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const pagedItems = items.slice((page - 1) * pageSize, page * pageSize);
  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-serif text-3xl font-semibold">Orders ({items.length})</h2>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={exportOrders}
            disabled={exporting}
            className="inline-flex items-center gap-2 rounded border border-[#2D5A47] px-4 py-2 text-sm font-semibold text-[#2D5A47] disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="export-orders-excel"
          >
            <Download size={15} /> {exporting ? "Exporting…" : "Export to Excel"}
          </button>
          <button
            type="button"
            onClick={printSelected}
            disabled={!selectedIds.length}
            className="inline-flex items-center gap-2 rounded bg-[#2D5A47] px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="bulk-print-selected"
          >
            <Printer size={15} /> Print selected ({selectedIds.length}/100)
          </button>
        </div>
      </div>
      <div className="mb-5 flex flex-wrap items-end gap-3" data-testid="order-date-filter">
        <label className="text-xs font-semibold text-[#64748B]">
          Orders per page
          <select value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }} className="mt-1 block rounded border border-[#E2DCD2] bg-white px-3 py-2 text-sm" data-testid="orders-page-size">
            {[25, 50, 100].map((size) => <option key={size} value={size}>{size}</option>)}
          </select>
        </label>
        <label className="text-xs font-semibold text-[#64748B]">
          Start date
          <input
            type="date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
            className="mt-1 block rounded border border-[#E2DCD2] bg-white px-3 py-2 text-sm text-[#1E2A24]"
            data-testid="orders-start-date"
          />
        </label>
        <label className="text-xs font-semibold text-[#64748B]">
          Status
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="mt-1 block rounded border border-[#E2DCD2] bg-white px-3 py-2 text-sm text-[#1E2A24]"
            data-testid="orders-status-filter"
          >
            <option value="">All statuses</option>
            {[
              ["created", "Pending"],
              ["confirmed", "Processing"],
              ["packed", "Ready to Ship"],
              ["shipped", "Shipped"],
              ["delivered", "Delivered"],
              ["cancelled", "Cancelled"],
            ].map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        {orderIdFilter && (
          <div className="pb-2 text-xs text-[#64748B]" data-testid="orders-direct-record-filter">
            Viewing order {orderIdFilter.slice(0, 8)}
          </div>
        )}
        <label className="text-xs font-semibold text-[#64748B]">
          End date
          <input
            type="date"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
            className="mt-1 block rounded border border-[#E2DCD2] bg-white px-3 py-2 text-sm text-[#1E2A24]"
            data-testid="orders-end-date"
          />
        </label>
        <button
          type="button"
          onClick={applyOrderFilters}
          className={`rounded border px-4 py-2 text-sm font-semibold transition-colors ${
            hasAppliedFilters && filtersAreCurrent
              ? "border-emerald-700 bg-emerald-700 text-white"
              : "border-[#2D5A47] text-[#2D5A47] hover:bg-[#EBF2EE]"
          }`}
          data-testid="apply-order-date-filter"
        >
          {hasAppliedFilters && filtersAreCurrent ? "Applied" : "Apply"}
        </button>
        <button
          type="button"
          onClick={clearDateFilter}
          className="rounded px-3 py-2 text-sm text-[#64748B]"
          data-testid="clear-order-date-filter"
        >
          Clear
        </button>
      </div>
      {hasAppliedFilters && (
        <div
          className="mb-5 inline-flex border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800"
          data-testid="orders-active-filter-confirmation"
        >
          Filters applied · {activeFilterSummary}
        </div>
      )}
      <div className="bg-white border border-[#E2DCD2] rounded-xl overflow-hidden">
        <div className="admin-table-scroll" data-testid="admin-orders-table-scroll">
          <table className="w-full min-w-[1120px] text-sm">
          <thead className="bg-[#F4F1EA] text-left text-[11px] uppercase text-[#64748B]">
            <tr>
              <th className="p-3"><input type="checkbox" checked={pagedItems.length > 0 && pagedItems.slice(0, 100).every((o) => selectedIds.includes(o.id))} onChange={toggleAllOrders} aria-label="Select up to 100 orders" data-testid="select-all-orders" /></th>
              <th className="p-3">Order</th>
              <th className="p-3">Customer</th>
              <th className="p-3">Total</th>
              <th className="p-3">Fulfillment</th>
              <th className="p-3">Payment &amp; refund</th>
              <th className="p-3">Status</th>
              <th className="p-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pagedItems.map((o) => {
              const tracking = o.tracking || {};
              const providerError = tracking.error;
              const providerErrorText = providerError === null || providerError === undefined
                ? ""
                : typeof providerError === "string"
                  ? providerError
                  : JSON.stringify(providerError, null, 2);
              const shippingDiagnostic = o.shipping_diagnostic;
              const retryableShipment = tracking.operation !== "cancel_order" && (
                tracking.status === "error" || (
                tracking.status === "creating"
                && tracking.attempted_at
                && Date.now() - new Date(tracking.attempted_at).getTime() >= 600000
                )
              );
              const retryableCancellation = (
                shippingDiagnostic?.cancellation_sync?.state === "failed"
                && tracking.operation === "cancel_order"
              );
              return (
              <tr key={o.id} className="border-t border-[#E2DCD2]" data-testid={`order-row-${o.id}`}>
                <td className="p-3"><input type="checkbox" checked={selectedIds.includes(o.id)} onChange={() => toggleOrder(o.id)} disabled={!selectedIds.includes(o.id) && selectedIds.length >= 100} aria-label={`Select order ${o.id.slice(0, 8)}`} data-testid={`select-order-${o.id}`} /></td>
                <td className="p-3 font-mono text-xs">
                  <div>{o.id.slice(0, 8)}</div>
                  <div className="mt-1 font-sans text-[10px] text-[#64748B]" data-testid={`order-created-at-${o.id}`}>
                    {o.created_at ? new Date(o.created_at).toLocaleString("en-IN") : "Date unavailable"}
                  </div>
                  {o.printed_at && (
                    <span
                      className="mt-1 inline-block rounded bg-emerald-50 px-1.5 py-0.5 font-sans text-[10px] font-semibold text-emerald-700"
                      data-testid={`printed-status-${o.id}`}
                    >
                      Printed
                    </span>
                  )}
                  {o.product_feedback?.map((review) => (
                    <div
                      key={review.id}
                      className="mt-2 text-[11px] text-[#8C6D46]"
                      data-testid={`order-product-feedback-${review.id}`}
                    >
                      <span className="font-semibold text-[#1E2A24]">{review.product_name}</span>
                      {` · ${"★".repeat(review.rating)}${"☆".repeat(5 - review.rating)}`}
                      {` · ${review.points_awarded} Eira Points`}
                      {review.comment ? ` · ${review.comment}` : " · No comment"}
                    </div>
                  ))}
                  {!o.product_feedback?.length && o.product_feedback_request_status && (
                    <div
                      className="mt-2 text-[10px] capitalize text-[#64748B]"
                      data-testid={`order-product-feedback-status-${o.id}`}
                    >
                      Product feedback {o.product_feedback_request_status}
                    </div>
                  )}
                  {o.order_status === "cancelled" && o.cancellation?.reason && (
                    <div
                      className="mt-2 border-l-2 border-red-300 pl-2 font-sans text-[11px] text-red-700"
                      data-testid={`admin-cancellation-reason-${o.id}`}
                    >
                      Reason: {o.cancellation.reason}
                    </div>
                  )}
                  {tracking.status === "error" && (
                    <div
                      className="mt-2 border-l-2 border-red-300 pl-2 font-sans text-[11px] text-red-700"
                      data-testid={`shiprocket-error-${o.id}`}
                    >
                      <div className="font-semibold">
                        Shiprocket error{tracking.error_status ? ` (${tracking.error_status})` : ""}
                      </div>
                      {shippingDiagnostic?.error_message && (
                        <div className="mt-1">
                          {shippingDiagnostic.error_message}
                        </div>
                      )}
                      {shippingDiagnostic?.validation_fields?.length > 0 && (
                        <div className="mt-1">
                          Check: {shippingDiagnostic.validation_fields.join(", ")}
                        </div>
                      )}
                      <details className="mt-1">
                        <summary className="cursor-pointer">View provider response</summary>
                        <pre
                          className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap break-words text-[10px]"
                          data-testid={`shiprocket-error-detail-${o.id}`}
                        >
                          {providerErrorText || "No provider response was returned."}
                        </pre>
                      </details>
                    </div>
                  )}
                  {shippingDiagnostic?.cancellation_sync && (
                    <div
                      className="mt-2 border-l-2 border-[#D99B26] pl-2 font-sans text-[11px] text-[#8C6D46]"
                      data-testid={`shiprocket-cancellation-sync-${o.id}`}
                    >
                      <div className="font-semibold">
                        {shippingDiagnostic.cancellation_sync.state === "synced"
                          ? "Shiprocket cancellation synced"
                          : shippingDiagnostic.cancellation_sync.state === "failed"
                            ? "Shiprocket cancellation failed"
                            : shippingDiagnostic.cancellation_sync.state === "blocked"
                              ? "Shiprocket cancellation blocked"
                              : "Shiprocket cancellation awaiting sync"}
                      </div>
                      <div>{shippingDiagnostic.cancellation_sync.message}</div>
                    </div>
                  )}
                </td>
                <td className="p-3">{o.user_email}</td>
                <td className="p-3 font-semibold">{inr(o.totals.total)}</td>
                <td className="p-3"><OrderFulfillmentDetails order={o} /></td>
                <td className="p-3"><OrderPaymentRefundDetails order={o} /></td>
                <td className="p-3">
                  <select
                    value={o.order_status}
                    onChange={(event) => confirmStatusChange(o, event.target.value)}
                    className="border border-[#E2DCD2] rounded px-2 py-1 text-xs"
                    data-testid={`admin-order-status-${o.id}`}
                  >
                    {["created", "confirmed", "packed", "shipped", "delivered", "cancelled"].map((s) => <option key={s}>{s}</option>)}
                  </select>
                </td>
                <td className="p-3 text-center">
                  <div className="inline-flex items-center gap-1">
                    <button
                      onClick={() => preview(o.id)}
                      title="Preview invoice without opening the print dialog"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#64748B] hover:bg-[#F4F1EA] px-3 py-1.5 rounded"
                      data-testid={`preview-order-${o.id}`}
                    >
                      <Eye size={14} /> Preview
                    </button>
                    <button
                      onClick={() => print(o.id)}
                      title="Print invoice"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2D5A47] hover:bg-[#EBF2EE] px-3 py-1.5 rounded"
                      data-testid={`print-order-${o.id}`}
                    >
                      <Printer size={14} /> Print
                    </button>
                    {o.order_status === "cancelled" && o.refund?.status === "pending" && (
                      <button
                        type="button"
                        onClick={() => retryRefund(o)}
                        disabled={retryingRefundId === o.id}
                        className="inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                        data-testid={`retry-refund-${o.id}`}
                      >
                        {retryingRefundId === o.id ? "Retrying…" : "Retry refund"}
                      </button>
                    )}
                    {retryableShipment && (
                      <button
                        type="button"
                        onClick={() => retryShipment(o)}
                        disabled={retryingShipmentId === o.id}
                        className="inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                        data-testid={`retry-shiprocket-${o.id}`}
                      >
                        {retryingShipmentId === o.id ? "Retrying…" : "Retry Shiprocket"}
                      </button>
                    )}
                    {retryableCancellation && (
                      <button
                        type="button"
                        onClick={() => retryShiprocketCancellation(o)}
                        disabled={retryingCancellationId === o.id}
                        className="inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                        data-testid={`retry-shiprocket-cancellation-${o.id}`}
                      >
                        {retryingCancellationId === o.id
                          ? "Retrying…"
                          : "Retry cancellation"}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-[#64748B]">No orders yet.</td></tr>}
          </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#E2DCD2] px-4 py-3 text-sm" data-testid="orders-footer-bar"><span data-testid="selected-orders-count">{selectedIds.length} order(s) selected</span><div className="flex items-center gap-2"><button type="button" disabled={page === 1} onClick={() => setPage((current) => current - 1)} className="border px-3 py-1 disabled:opacity-40" data-testid="orders-page-previous">Previous</button><span>Page {page} of {totalPages}</span><button type="button" disabled={page === totalPages} onClick={() => setPage((current) => current + 1)} className="border px-3 py-1 disabled:opacity-40" data-testid="orders-page-next">Next</button></div></div>
      </div>
    </div>
  );
};

const CustomersTab = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/customers").then((r) => setItems(r.data)); }, []);
  return (
    <div>
      <h2 className="font-serif text-3xl font-semibold mb-6">Customers ({items.length})</h2>
      <div className="bg-white border border-[#E2DCD2] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F1EA] text-left"><tr><th className="p-3">Name</th><th>Email</th><th>Provider</th><th>Eira Points</th><th>GST Details</th></tr></thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-t border-[#E2DCD2]"><td className="p-3">{u.name}</td><td>{u.email}</td><td className="capitalize">{u.provider || "local"}</td><td data-testid={`customer-eira-points-${u.id}`}><span className="font-semibold text-[#2D5A47]">{u.eira_points?.available_points || 0}</span><span className="ml-1 text-xs text-[#64748B]">available · {u.eira_points?.pending_points || 0} pending</span></td><td data-testid={`customer-gst-details-${u.id}`}>{u.gst_details?.company_name || u.gst_details?.gstin ? <div className="text-xs"><div className="font-semibold">{u.gst_details.company_name || "Company"}</div><div>{u.gst_details.gstin || "GSTIN not provided"}</div>{u.gst_details.company_address && <div className="mt-1 text-[#64748B]">{u.gst_details.company_address}</div>}</div> : <span className="text-xs text-[#64748B]">Not provided</span>}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const BannersTab = () => {
  const { requestDelete } = useAdminDeleteConfirmation();
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ title: "", subtitle: "", cta_text: "Shop Now", cta_link: "/shop", image_desktop: "", order: 0, active: true });
  const [editingId, setEditingId] = useState(null);
  const load = useCallback(
    () => api.get("/admin/banners").then((response) => setItems(response.data)),
    [],
  );
  useEffect(() => { load(); }, [load]);
  useEffect(() => { window.scrollTo({ top: 0, left: 0 }); }, [editingId]);
  const reset = () => {
    window.scrollTo({ top: 0, left: 0 });
    setEditingId(null);
    setF({ title: "", subtitle: "", cta_text: "Shop Now", cta_link: "/shop", image_desktop: "", order: 0, active: true });
  };
  const save = async () => {
    if (!f.title.trim()) {
      showAdminFieldErrors("Headline title is required", ["banner-title-input"]);
      return;
    }
    if (!f.image_desktop) {
      showAdminFieldErrors("Upload a banner image first", ["banner-image-upload"]);
      return;
    }
    await (editingId ? api.put(`/banners/${editingId}`, f) : api.post("/banners", f));
    toast.success(editingId ? "Banner updated" : "Banner added");
    reset();
    load();
  };
  const edit = (banner) => { window.scrollTo({ top: 0, left: 0 }); setEditingId(banner.id); setF({ ...banner }); };
  const toggle = async (banner) => { await api.put(`/banners/${banner.id}`, { ...banner, active: !banner.active }); load(); };
  const del = (id) => requestDelete(async () => {
    await api.delete(`/banners/${id}`);
    await load();
  });
  return (
    <div>
      <h2 className="font-serif text-3xl font-semibold mb-6">Banners</h2>
      <div className="bg-white border border-[#E2DCD2] rounded-xl p-5 mb-6">
        <h3 className="font-semibold mb-4 text-lg">{editingId ? "Edit Banner" : "Add Banner"}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Headline title">
            <input placeholder="e.g. Diwali Kitchen Sale" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="banner-title-input" />
          </Field>
          <Field label="Subtitle / supporting line">
            <input placeholder="e.g. Up to 30% off · Free shipping over ₹499" value={f.subtitle} onChange={(e) => setF({ ...f, subtitle: e.target.value })} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" />
          </Field>
          <Field label="CTA button text">
            <input placeholder="Shop Now" value={f.cta_text} onChange={(e) => setF({ ...f, cta_text: e.target.value })} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" />
          </Field>
          <Field label="CTA button link" hint="e.g. /shop?category=kitchen">
            <input placeholder="/shop" value={f.cta_link} onChange={(e) => setF({ ...f, cta_link: e.target.value })} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" />
          </Field>
          <div className="md:col-span-2">
            <MediaPicker
              images={f.image_desktop ? [f.image_desktop] : []}
              setImages={(images) => setF({ ...f, image_desktop: images[0] || "" })}
              maxImages={1}
              allowVideo={false}
              imageHint="1920 × 800 px · JPG/PNG/WebP · ≤ 8 MB"
              testId="banner-image-upload"
            />
          </div>
          <div className="md:col-span-2 flex gap-3">
            <button onClick={save} className="bg-[#2D5A47] hover:bg-[#214435] text-white rounded-lg px-5 py-2.5 text-sm font-semibold" data-testid="save-banner">{editingId ? "Save Banner" : "Create Banner"}</button>
            {editingId && <button onClick={reset} className="text-sm text-[#64748B]" data-testid="cancel-banner-edit">Cancel</button>}
          </div>
        </div>
      </div>
      <div className="grid gap-3">
        {items.map((b) => (
          <div key={b.id} className="bg-white border border-[#E2DCD2] rounded-xl p-4 flex items-center gap-4">
            <img src={b.image_desktop} className="w-24 h-16 rounded object-cover" />
            <div className="flex-1"><div className="font-semibold">{b.title}</div><div className="text-xs text-[#64748B]">{b.subtitle}</div></div>
            <div className="flex items-center gap-1">
              <button onClick={() => edit(b)} className="p-2 text-[#2D5A47]" data-testid={`edit-banner-${b.id}`}><Pencil size={16} /></button>
              <button onClick={() => toggle(b)} className={`rounded p-2 text-white ${b.active ? "bg-emerald-600" : "bg-[#7F1D1D]"}`} data-testid={`toggle-banner-${b.id}`} title={b.active ? "Disable" : "Enable"}><Power size={16} /></button>
              <button onClick={() => del(b.id)} className="text-red-500 p-2" data-testid={`delete-banner-${b.id}`}><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const CouponsTab = ({ onNavigate }) => {
  const { requestDelete } = useAdminDeleteConfirmation();
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [eligibleProductSearch, setEligibleProductSearch] = useState("");
  const [freeItemQuery, setFreeItemQuery] = useState("");
  const [freeItemPickerOpen, setFreeItemPickerOpen] = useState(false);
  const [editingCouponId, setEditingCouponId] = useState(null);
  const emptyCoupon = {
    code: "",
    coupon_type: "promo",
    usage_mode: "recurring",
    kind: "percent",
    value: 10,
    min_order: 0,
    product_ids: [],
    free_product_id: "",
    free_variant_label: "",
    description: "",
    send_to_active_customers: false,
  };
  const [f, setF] = useState(emptyCoupon);
  const load = () => api.get("/admin/coupons").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/admin/products?include_scheduled=1")
      .then((r) => setProducts(r.data.items || r.data));
  }, []);

  const giftProduct = products.find((product) => product.id === f.free_product_id);
  const giftVariants = giftProduct?.variants || [];
  const isBogo = f.kind === "bogo";
  const isFreeItem = f.kind === "free_item";
  const isValueOffer = f.kind === "percent" || f.kind === "flat";
  const couponSelectableProducts = products.filter((product) => product.active !== false);
  const filterProducts = (searchTerm) => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return couponSelectableProducts;
    return couponSelectableProducts.filter((product) => product.name.toLowerCase().includes(query));
  };
  const eligibleProducts = filterProducts(eligibleProductSearch);
  const freeItemProducts = filterProducts(freeItemQuery);

  const toggleEligibleProduct = (productId) => {
    setF((current) => ({
      ...current,
      product_ids: current.product_ids.includes(productId)
        ? current.product_ids.filter((id) => id !== productId)
        : [...current.product_ids, productId],
    }));
  };

  const resetCouponForm = () => {
    setF(emptyCoupon);
    setEditingCouponId(null);
    setEligibleProductSearch("");
    setFreeItemQuery("");
    setFreeItemPickerOpen(false);
  };

  const beginEdit = (coupon) => {
    const selectedGift = products.find((product) => product.id === coupon.free_product_id);
    setF({
      ...emptyCoupon,
      code: coupon.code || "",
      coupon_type: coupon.coupon_type || "promo",
      usage_mode: coupon.usage_mode || "recurring",
      kind: coupon.kind || "percent",
      value: coupon.value || 0,
      min_order: coupon.min_order || 0,
      max_discount: coupon.max_discount ?? null,
      product_ids: coupon.product_ids || [],
      free_product_id: coupon.free_product_id || "",
      free_variant_label: coupon.free_variant_label || "",
      starts_at: coupon.starts_at || null,
      ends_at: coupon.ends_at || null,
      active: coupon.active !== false,
      description: coupon.description || "",
      send_to_active_customers: false,
    });
    setEditingCouponId(coupon.id);
    setEligibleProductSearch("");
    setFreeItemQuery(selectedGift?.name || "");
    setFreeItemPickerOpen(false);
  };

  const saveCoupon = async () => {
    if (!f.code.trim()) {
      showAdminFieldErrors("Coupon code is required", ["coupon-code-input"]);
      return;
    }
    if (isBogo && !f.product_ids.length) {
      showAdminFieldErrors("Select at least one eligible product for this offer", [
        "coupon-product-selector",
      ]);
      return;
    }
    if (isFreeItem && !f.free_product_id) {
      showAdminFieldErrors("Select the product to give as the free item", [
        "coupon-free-item-combobox",
      ]);
      return;
    }
    if (isFreeItem && giftVariants.length > 0 && !f.free_variant_label) {
      showAdminFieldErrors("Select the free item variant", ["coupon-free-item-variant"]);
      return;
    }
    try {
      const isEditing = Boolean(editingCouponId);
      let response;
      if (editingCouponId) {
        response = await api.put(`/coupons/${editingCouponId}`, f);
      } else {
        response = await api.post("/coupons", f);
      }
      await load();
      resetCouponForm();
      toast.success(
        response.data.coupon_email_campaign_id
          ? "Coupon saved. Customer email campaign queued."
          : isEditing
            ? "Coupon updated"
            : "Coupon created",
      );
    } catch (error) {
      toast.error(formatError(error));
    }
  };
  const del = (id) => requestDelete(async () => {
    await api.delete(`/coupons/${id}`);
    await load();
  });
  const toggle = async (coupon) => { await api.put(`/coupons/${coupon.id}/active`, { active: !coupon.active }); load(); };

  const couponLabel = (coupon) => {
    if (coupon.kind === "bogo") return "Buy 1 Get 1 Free";
    if (coupon.kind === "free_shipping") return "Free Delivery";
    if (coupon.kind === "free_item") return "Free Item";
    return `${coupon.coupon_type || "basket"} · ${coupon.kind} · ${coupon.value}${
      coupon.kind === "percent" ? "%" : ""
    }`;
  };

  return (
    <div>
      <h2 className="font-serif text-3xl font-semibold mb-6">Coupons</h2>
      <div className="bg-white border border-[#E2DCD2] rounded-xl p-5 mb-6">
        <h3 className="font-semibold mb-4 text-lg">
          {editingCouponId ? "Edit Coupon" : "Add Coupon"}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Coupon code" hint="Auto-uppercased">
            <input
              placeholder="WELCOME10"
              value={f.code}
              onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })}
              className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm font-mono"
              data-testid="coupon-code-input"
            />
          </Field>
          <Field label="Offer type">
            <select
              value={f.kind}
              onChange={(e) => setF({ ...f, kind: e.target.value })}
              className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm"
              data-testid="coupon-offer-type"
            >
              <option value="percent">% Percent off</option>
              <option value="flat">₹ Flat amount off</option>
              <option value="free_shipping">Free shipping</option>
              <option value="bogo">Buy 1 Get 1 Free</option>
              <option value="free_item">Free item</option>
            </select>
          </Field>
          <Field label="Discount applies to">
            <select
              value={f.coupon_type}
              onChange={(e) => setF({ ...f, coupon_type: e.target.value })}
              className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm"
              data-testid="coupon-type"
            >
              <option value="promo">Promo Code</option>
              <option value="item">Item Discount</option>
              <option value="basket">Basket Discount</option>
            </select>
          </Field>
          <Field label="Usage">
            <select
              value={f.usage_mode}
              onChange={(e) => setF({ ...f, usage_mode: e.target.value })}
              className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm"
              data-testid="coupon-usage-mode"
            >
              <option value="one_time">One-time use</option>
              <option value="recurring">Recurring</option>
              <option value="welcomeback">Welcome back (180 days)</option>
            </select>
          </Field>
          {isValueOffer && (
            <Field label="Value" hint={f.kind === "percent" ? "e.g. 10 = 10% off" : "e.g. 100 = ₹100 off"}>
              <input
                placeholder={f.kind === "percent" ? "10" : "100"}
                type="number"
                min="0"
                value={f.value || ""}
                onChange={(e) => setF({ ...f, value: +e.target.value })}
                className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm"
                data-testid="coupon-value-input"
              />
            </Field>
          )}
          <Field label="Minimum order (₹)" hint="0 for no minimum">
            <input
              placeholder="0"
              type="number"
              min="0"
              value={f.min_order || ""}
              onChange={(e) => setF({ ...f, min_order: +e.target.value })}
              className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm"
              data-testid="coupon-minimum-order-input"
            />
          </Field>
          <label className="flex items-center gap-3 border border-[#C8BFB0] bg-[#FDFBF7] px-3 py-2.5 md:col-span-2">
            <input
              type="checkbox"
              checked={Boolean(f.send_to_active_customers)}
              onChange={(event) => setF({
                ...f,
                send_to_active_customers: event.target.checked,
              })}
              className="h-4 w-4 accent-[#2D5A47]"
              data-testid="coupon-send-email-toggle"
            />
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-[#1E2A24]">
                Send email to all active customers
              </span>
              <span className="block text-xs text-[#64748B]">
                Sends this offer to customers with at least one order. 
                <button
                  type="button"
                  onClick={() => onNavigate("email-templates", "coupon_announcement")}
                  className="font-semibold text-[#2D5A47] hover:underline"
                  data-testid="coupon-email-template-link"
                >
                  Edit email template
                </button>
              </span>
            </span>
          </label>
          <div className="flex items-end">
            <button
              onClick={saveCoupon}
              className="w-full bg-[#2D5A47] hover:bg-[#214435] text-white rounded-lg py-2.5 text-sm font-semibold"
              data-testid="create-coupon"
            >
              {editingCouponId ? "Save Changes" : "Add Coupon"}
            </button>
          </div>
        </div>
        {editingCouponId && (
          <button
            type="button"
            onClick={resetCouponForm}
            className="mt-4 text-sm font-semibold text-[#C86D42] hover:underline"
            data-testid="cancel-coupon-edit"
          >
            Cancel editing
          </button>
        )}
        {(isBogo || (f.coupon_type === "item" && !isFreeItem)) && (
          <div className="mt-4 max-h-40 overflow-y-auto border border-[#E2DCD2] rounded-lg p-3" data-testid="coupon-product-selector">
            <p className="mb-2 text-xs text-[#64748B]">
              {isBogo
                ? "Choose eligible products. In each pair, the lower-priced eligible item is free."
                : "Choose products eligible for this item discount."}
            </p>
            <input
              type="search"
              value={eligibleProductSearch}
              onChange={(event) => setEligibleProductSearch(event.target.value)}
              placeholder="Search eligible products"
              className="mb-3 w-full border border-[#E2DCD2] px-3 py-2 text-sm outline-none focus:border-[#2D5A47]"
              data-testid="coupon-eligible-product-search"
            />
            {eligibleProducts.map((product) => (
              <label key={product.id} className="flex items-center gap-2 py-1 text-sm">
                <input
                  type="checkbox"
                  checked={f.product_ids.includes(product.id)}
                  onChange={() => toggleEligibleProduct(product.id)}
                  data-testid={`coupon-eligible-product-${product.id}`}
                />
                {product.name}
              </label>
            ))}
            {!eligibleProducts.length && (
              <p className="py-2 text-sm text-[#64748B]" data-testid="coupon-product-search-empty">
                No matching products found.
              </p>
            )}
          </div>
        )}
        {isFreeItem && (
          <div className="mt-4 grid gap-4 border border-[#E2DCD2] rounded-lg p-4 md:grid-cols-2" data-testid="coupon-free-item-selector">
            <div className="relative">
              <label className="block text-sm">
                <span className="font-medium text-[#1E2A24]">Fixed free item</span>
                <input
                  type="search"
                  value={freeItemQuery}
                  onFocus={() => setFreeItemPickerOpen(true)}
                  onChange={(event) => {
                    setFreeItemQuery(event.target.value);
                    setFreeItemPickerOpen(true);
                    setF({
                      ...f,
                      free_product_id: "",
                      free_variant_label: "",
                    });
                  }}
                  placeholder="Search and select a product"
                  className="mt-1 w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm"
                  data-testid="coupon-free-item-combobox"
                />
              </label>
              {freeItemPickerOpen && (
                <div
                  className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto border border-[#E2DCD2] bg-white shadow-lg"
                  data-testid="coupon-free-item-options"
                >
                  {freeItemProducts.map((product) => (
                    <button
                      key={product.id}
                      type="button"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        setF({
                          ...f,
                          free_product_id: product.id,
                          free_variant_label: "",
                        });
                        setFreeItemQuery(product.name);
                        setFreeItemPickerOpen(false);
                      }}
                      className="block w-full border-b border-[#F4F1EA] px-3 py-2 text-left text-sm last:border-b-0 hover:bg-[#F4F1EA]"
                      data-testid={`coupon-free-item-option-${product.id}`}
                    >
                      {product.name}
                    </button>
                  ))}
                  {!freeItemProducts.length && (
                    <p className="px-3 py-2 text-sm text-[#64748B]" data-testid="coupon-free-item-empty">
                      No matching products found.
                    </p>
                  )}
                </div>
              )}
            </div>
            {giftProduct && (
              <button
                type="button"
                onClick={() => {
                  setF({ ...f, free_product_id: "", free_variant_label: "" });
                  setFreeItemQuery("");
                  setFreeItemPickerOpen(false);
                }}
                className="self-end justify-self-start text-sm font-semibold text-[#C86D42] hover:underline"
                data-testid="clear-coupon-free-item"
              >
                Clear selected item
              </button>
            )}
            {giftVariants.length > 0 && (
              <Field label="Free item variant">
                <select
                  value={f.free_variant_label}
                  onChange={(e) => setF({ ...f, free_variant_label: e.target.value })}
                  className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm"
                  data-testid="coupon-free-item-variant"
                >
                  <option value="">Select a variant</option>
                  {giftVariants.map((variant) => (
                    <option key={variant.label} value={variant.label}>{variant.label}</option>
                  ))}
                </select>
              </Field>
            )}
          </div>
        )}
      </div>
      <div className="grid gap-2">
        {items.map((c) => (
          <div key={c.id} className="bg-white border border-[#E2DCD2] rounded-xl p-4 flex items-center gap-3">
            <span className="font-mono font-bold text-[#C86D42]">{c.code}</span>
            <span className="text-sm">{couponLabel(c)}</span>
            <span className="text-xs text-[#64748B] ml-auto">min {inr(c.min_order)}</span>
            <button
              onClick={() => beginEdit(c)}
              className="p-1 text-[#2D5A47]"
              data-testid={`edit-coupon-${c.id}`}
              title="Edit coupon"
            >
              <Pencil size={14} />
            </button>
            <button onClick={() => toggle(c)} className={`rounded p-1.5 text-white ${c.active ? "bg-emerald-600" : "bg-[#7F1D1D]"}`} data-testid={`toggle-coupon-${c.id}`} title={c.active ? "Disable" : "Enable"}><Power size={14} /></button>
            <button onClick={() => del(c.id)} className="text-red-500 p-1" data-testid={`delete-coupon-${c.id}`}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const CategoriesTab = () => {
  const { requestDelete } = useAdminDeleteConfirmation();
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ name: "", slug: "", image: "", description: "", order: 0, active: true });
  const [editingId, setEditingId] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { delay: 180, tolerance: 5 } }),
  );
  const load = () => api.get("/admin/categories").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { window.scrollTo({ top: 0, left: 0 }); }, [editingId]);
  const reset = () => { window.scrollTo({ top: 0, left: 0 }); setEditingId(null); setF({ name: "", slug: "", image: "", description: "", order: items.length, active: true }); };
  const save = async () => {
    const missingFields = [
      !f.name.trim() && "category-name-input",
      !f.slug.trim() && "category-slug-input",
      !f.image && "category-image-upload",
    ].filter(Boolean);
    if (missingFields.length) {
      showAdminFieldErrors("Name, URL slug, and category image are required", missingFields);
      return;
    }
    await (editingId ? api.put(`/categories/${editingId}`, f) : api.post("/categories", f));
    toast.success(editingId ? "Category updated" : "Category created");
    reset();
    load();
  };
  const edit = (category) => { window.scrollTo({ top: 0, left: 0 }); setEditingId(category.id); setF({ ...category }); };
  const toggle = async (category) => { await api.put(`/categories/${category.id}`, { ...category, active: !category.active }); load(); };
  const del = (id) => requestDelete(async () => {
    await api.delete(`/categories/${id}`);
    await load();
  });
  const reorder = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const previousItems = items;
    const oldIndex = previousItems.findIndex((category) => category.id === active.id);
    const newIndex = previousItems.findIndex((category) => category.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const nextItems = arrayMove(previousItems, oldIndex, newIndex);
    setItems(nextItems);
    try {
      const { data } = await api.put("/categories/reorder", {
        category_ids: nextItems.map((category) => category.id),
      });
      setItems(data);
      toast.success("Category positions saved");
    } catch (error) {
      setItems(previousItems);
      toast.error(formatError(error));
    }
  };
  return (
    <div>
      <h2 className="font-serif text-3xl font-semibold mb-6">Categories</h2>
      <div className="bg-white border border-[#E2DCD2] rounded-xl p-5 mb-6">
        <h3 className="font-semibold mb-4 text-lg">{editingId ? "Edit Category" : "Add Category"}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Category name">
            <input placeholder="e.g. Household" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="category-name-input" />
          </Field>
          <Field label="URL slug" hint="Auto-generated from the name — edit if needed">
            <input placeholder="household" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm font-mono" data-testid="category-slug-input" />
          </Field>
          <div className="md:col-span-2">
            <MediaPicker
              images={f.image ? [f.image] : []}
              setImages={(images) => setF({ ...f, image: images[0] || "" })}
              maxImages={1}
              allowVideo={false}
              imageHint="800 × 800 px · JPG/PNG/WebP · ≤ 8 MB"
              testId="category-image-upload"
            />
          </div>
          <div className="md:col-span-2 flex gap-3">
            <button onClick={save} className="bg-[#2D5A47] hover:bg-[#214435] text-white rounded-lg px-5 py-2.5 text-sm font-semibold" data-testid="save-category">{editingId ? "Save Category" : "Add Category"}</button>
            {editingId && <button onClick={reset} className="text-sm text-[#64748B]" data-testid="cancel-category-edit">Cancel</button>}
          </div>
        </div>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={reorder}
      >
        <SortableContext items={items.map((category) => category.id)} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="admin-category-grid">
            {items.map((category) => (
              <SortableCategoryCard
                key={category.id}
                category={category}
                onDelete={del}
                onEdit={edit}
                onToggle={toggle}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

const CampaignsTab = () => {
  const { requestDelete } = useAdminDeleteConfirmation();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [prods, setProds] = useState([]);
  const [mediaBusy, setMediaBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [f, setF] = useState({
    slug: "", title: "", tagline: "", hero_image: "", hero_video: null, body_html: "",
    cta_text: "Shop Now", cta_link: "/shop", product_ids: [], category_slug: "",
    start_date: "", end_date: "", theme_color: "#2D5A47", active: true,
  });
  const load = () => api.get("/admin/campaigns").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/products?limit=200").then((r) => setProds(r.data.items));
  }, []);
  useEffect(() => { window.scrollTo({ top: 0, left: 0 }); }, [editingId]);
  const reset = () => {
    window.scrollTo({ top: 0, left: 0 });
    setEditingId(null);
    setF({ ...f, slug: "", title: "", tagline: "", hero_image: "", hero_video: null, body_html: "", start_date: "", end_date: "", active: true });
  };
  const save = async () => {
    const missingFields = [
      !f.title.trim() && "camp-title",
      !f.slug.trim() && "camp-slug",
      !f.hero_image && !f.hero_video && "camp-media",
    ].filter(Boolean);
    if (missingFields.length) {
      showAdminFieldErrors("Title, URL slug, and hero image or video are required", missingFields);
      return;
    }
    try {
      const payload = {
        ...f,
        start_date: f.start_date ? new Date(f.start_date).toISOString() : null,
        end_date: f.end_date ? new Date(f.end_date).toISOString() : null,
        category_slug: f.category_slug || null,
      };
      await (editingId ? api.put(`/campaigns/${editingId}`, payload) : api.post("/campaigns", payload));
      toast.success(editingId ? "Campaign updated" : "Campaign published");
      reset();
      load();
    } catch (e) { toast.error(formatError(e)); }
  };
  const del = (id) => requestDelete(async () => {
    await api.delete(`/campaigns/${id}`);
    await load();
  });
  const edit = (campaign) => {
    window.scrollTo({ top: 0, left: 0 });
    setEditingId(campaign.id);
    setF({
      ...campaign,
      start_date: campaign.start_date ? campaign.start_date.slice(0, 16) : "",
      end_date: campaign.end_date ? campaign.end_date.slice(0, 16) : "",
    });
  };
  const toggle = async (campaign) => {
    await api.put(`/campaigns/${campaign.id}`, { ...campaign, active: !campaign.active });
    load();
  };
  const toggleProd = (id) => setF((p) => ({ ...p, product_ids: p.product_ids.includes(id) ? p.product_ids.filter((x) => x !== id) : [...p.product_ids, id] }));
  const heroImages = f.hero_image ? [f.hero_image] : [];
  return (
    <div>
      <h2 className="font-serif text-3xl font-semibold mb-6">Festival Campaigns</h2>
      <div className="bg-white border border-[#E2DCD2] rounded-xl p-5 mb-6">
        <h3 className="font-semibold mb-4 text-lg">{editingId ? "Edit Campaign" : "Create New Campaign"}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Campaign title">
            <input placeholder="e.g. Diwali Kitchen Sale" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value, slug: f.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") })} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="camp-title" />
          </Field>
          <Field label="URL slug" hint="Public URL will be /campaign/<slug>">
            <input placeholder="diwali-kitchen-sale" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm font-mono" data-testid="camp-slug" />
          </Field>
          <Field label="Tagline / short description" span="md:col-span-2">
            <input placeholder="One-liner shown under the title" value={f.tagline} onChange={(e) => setF({ ...f, tagline: e.target.value })} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" />
          </Field>
          <Field label="CTA button text">
            <input placeholder="Shop Now" value={f.cta_text} onChange={(e) => setF({ ...f, cta_text: e.target.value })} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" />
          </Field>
          <Field label="CTA button link" hint="e.g. /shop?category=kitchen">
            <input placeholder="/shop" value={f.cta_link} onChange={(e) => setF({ ...f, cta_link: e.target.value })} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" />
          </Field>
          <Field label="Start date & time" hint="Leave empty for immediate">
            <input type="datetime-local" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="camp-start" />
          </Field>
          <Field label="End date & time" hint="Optional">
            <input type="datetime-local" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="camp-end" />
          </Field>
          <Field label="Feature category" hint="Optional — pulls all products in this category">
            <select value={f.category_slug} onChange={(e) => setF({ ...f, category_slug: e.target.value })} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm">
              <option value="">— None —</option>
              {cats.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Theme accent colour">
            <input type="color" value={f.theme_color} onChange={(e) => setF({ ...f, theme_color: e.target.value })} className="border border-[#E2DCD2] rounded-lg h-10 w-full" />
          </Field>
          <Field label="Rich body HTML" hint="Optional — appears below the hero" span="md:col-span-2">
            <textarea placeholder="<h2>About this sale</h2>…" value={f.body_html} onChange={(e) => setF({ ...f, body_html: e.target.value })} rows={3} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm font-mono" />
          </Field>
        </div>
        <div className="mt-4">
          <label className="text-xs font-semibold uppercase tracking-wider text-[#8C6D46] block mb-1">Campaign Hero Media</label>
          <div className="text-[11px] text-[#8C6D46] mb-2">1 hero image + optional 1 video. Recommended: <b>1920 × 900 px</b> hero image.</div>
          <MediaPicker
            images={heroImages}
            setImages={(imgs) => setF({ ...f, hero_image: imgs[0] || "" })}
            video={f.hero_video}
            setVideo={(v) => setF({ ...f, hero_video: v })}
            maxImages={1}
            allowVideo={true}
            imageHint="1920 × 900 px · JPG/PNG/WebP · ≤ 8 MB"
            videoHint="1920 × 1080 · MP4/WebM · ≤ 50 MB · ≤ 30 s"
            onBusyChange={setMediaBusy}
            testId="camp-media"
          />
        </div>
        <div className="mt-4">
          <div className="text-sm font-semibold mb-2">Feature products ({f.product_ids.length} selected)</div>
          <div className="max-h-48 overflow-y-auto border border-[#E2DCD2] rounded-lg p-2 grid grid-cols-2 md:grid-cols-3 gap-2">
            {prods.map((p) => (
              <label key={p.id} className={`flex items-center gap-2 p-2 rounded cursor-pointer text-xs ${f.product_ids.includes(p.id) ? "bg-[#EBF2EE]" : "hover:bg-[#F4F1EA]"}`}>
                <input type="checkbox" checked={f.product_ids.includes(p.id)} onChange={() => toggleProd(p.id)} />
                <img src={p.images?.[0]} className="w-8 h-8 rounded object-cover" />
                <span className="line-clamp-1">{p.name}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="mt-4">
          <CopyCheckPanel
            contentType="campaign"
            title={f.title}
            text={`${f.tagline}\n${f.body_html}`}
            ctaText={f.cta_text}
            testId="campaign"
          />
        </div>
        <div className="mt-4 flex gap-3">
          <button onClick={save} disabled={mediaBusy} className="bg-[#C86D42] hover:bg-[#AE5931] disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-2.5 rounded-lg text-sm font-semibold" data-testid="save-campaign">
            {mediaBusy ? "Uploading media…" : editingId ? "Save Campaign" : "Publish Campaign"}
          </button>
          {editingId && <button onClick={reset} className="text-sm text-[#64748B]" data-testid="cancel-campaign-edit">Cancel</button>}
        </div>
      </div>
      <div className="grid gap-3">
        {items.length === 0 && <div className="bg-white border border-[#E2DCD2] rounded-xl p-8 text-center text-[#64748B]">No campaigns yet. Create your first Diwali/Holi sale above.</div>}
        {items.map((c) => (
          <div key={c.id} className="bg-white border border-[#E2DCD2] rounded-xl overflow-hidden flex" data-testid={`campaign-${c.slug}`}>
            {c.hero_video ? (
              <video src={c.hero_video} muted className="w-32 h-32 object-cover" />
            ) : (
              <img src={c.hero_image} className="w-32 h-32 object-cover" />
            )}
            <div className="flex-1 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs tracking-widest uppercase text-[#8C6D46]">/campaign/{c.slug} {c.hero_video && "· 🎥"}</div>
                  <div className="font-serif text-xl">{c.title}</div>
                  <div className="text-sm text-[#64748B]">{c.tagline}</div>
                </div>
                <div className="flex items-center gap-1"><button onClick={() => edit(c)} className="p-2 text-[#2D5A47]" data-testid={`edit-campaign-${c.id}`}><Pencil size={16} /></button><button onClick={() => toggle(c)} className={`rounded p-2 text-white ${c.active ? "bg-emerald-600" : "bg-[#7F1D1D]"}`} data-testid={`toggle-campaign-${c.id}`} title={c.active ? "Disable" : "Enable"}><Power size={16} /></button><button onClick={() => del(c.id)} className="p-2 text-red-500" data-testid={`delete-campaign-${c.id}`}><Trash2 size={16} /></button></div>
              </div>
              <div className="text-xs mt-2 flex gap-4 text-[#64748B]">
                {c.start_date && <span>Starts: {new Date(c.start_date).toLocaleString("en-IN")}</span>}
                {c.end_date && <span>Ends: {new Date(c.end_date).toLocaleString("en-IN")}</span>}
                <span>{c.product_ids?.length || 0} products</span>
                <a href={`/campaign/${c.slug}`} target="_blank" rel="noreferrer" className="text-[#C86D42] font-semibold">View →</a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const BrandingCard = () => {
  const { logo_url, logo_alt, tagline, refresh } = useBranding();
  const [tag, setTag] = useState(tagline || "");
  const [alt, setAlt] = useState(logo_alt || "");
  const [busy, setBusy] = useState(false);
  const [savingText, setSavingText] = useState(false);

  useEffect(() => { setTag(tagline || ""); setAlt(logo_alt || ""); }, [tagline, logo_alt]);

  const uploadLogo = async (files) => {
    const file = files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Logo must be an image (PNG/SVG/WebP)"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/admin/branding", { logo_url: data.url });
      await refresh();
      toast.success("Logo updated everywhere");
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const saveText = async () => {
    setSavingText(true);
    try {
      await api.put("/admin/branding", { logo_alt: alt, tagline: tag });
      await refresh();
      toast.success("Branding saved");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSavingText(false);
    }
  };

  const clearLogo = async () => {
    try {
      await api.put("/admin/branding", { logo_url: "" });
      await refresh();
      toast.success("Logo cleared — using default wordmark");
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="bg-white border border-[#E2DCD2] rounded-xl p-6 mb-6" data-testid="settings-branding">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Image size={18} className="text-[#2D5A47]" />
          <h3 className="font-semibold text-lg">Site Logo & Branding</h3>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-6 items-start">
        <div>
          <div className="text-xs text-[#64748B] mb-2">Current logo (appears in header & footer everywhere)</div>
          <div className="rounded-xl bg-[#F4F1EA] border border-[#E2DCD2] h-40 flex items-center justify-center p-4">
            {logo_url ? (
              <img src={logo_url} alt={logo_alt || "Logo"} className="max-h-full max-w-full object-contain" data-testid="branding-current-logo" />
            ) : (
              <div className="text-center text-[#8C6D46]">
                <div className="font-serif text-3xl italic">{logo_alt || "Eira"}</div>
                <div className="text-[10px] tracking-widest uppercase mt-1">Default wordmark</div>
              </div>
            )}
          </div>
          <div className="flex gap-2 mt-3">
            <label className={`flex-1 flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold cursor-pointer border-2 border-dashed transition-colors ${busy ? "border-[#2D5A47] bg-[#EBF2EE] cursor-wait" : "border-[#2D5A47] hover:bg-[#EBF2EE] text-[#2D5A47]"}`} data-testid="upload-logo-btn">
              {busy ? <><Loader2 className="animate-spin" size={16} /> Uploading…</> : <><UploadIcon size={16} /> Upload new logo</>}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files)} disabled={busy} data-testid="upload-logo-input" />
            </label>
            {logo_url && (
              <button onClick={clearLogo} className="px-3 py-2.5 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50" data-testid="clear-logo-btn">
                <Trash2 size={16} />
              </button>
            )}
          </div>
          <div className="text-xs text-[#8C6D46] mt-2">PNG or SVG recommended · transparent background · at least 400px tall</div>
        </div>
        <div>
          <label className="text-xs text-[#64748B]">Business / brand name</label>
          <input value={alt} onChange={(e) => setAlt(e.target.value)} placeholder="Eira — Goodness Lives Here" className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm mb-3" data-testid="logo-alt" />
          <label className="text-xs text-[#64748B]">Brand tagline (fallback text below wordmark)</label>
          <input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="Goodness Lives Here" className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm mb-3" data-testid="brand-tagline" />
          <button
            onClick={saveText}
            disabled={savingText}
            className="bg-[#2D5A47] hover:bg-[#214435] disabled:opacity-50 text-white px-5 py-2 rounded-lg text-sm font-semibold"
            data-testid="save-branding"
          >
            {savingText ? "Saving…" : "Save branding"}
          </button>
        </div>
      </div>
    </div>
  );
};

const SiteContactCard = () => {
  const { contact_mobile, contact_email, address, refresh } = useBranding();
  const [f, setF] = useState({ contact_mobile: "", contact_email: "", address: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setF({
      contact_mobile: contact_mobile || "",
      contact_email: contact_email || "",
      address: address || "",
    });
  }, [contact_mobile, contact_email, address]);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/branding", {
        ...f,
        contact_mobile: formatIndianPhone(f.contact_mobile),
      });
      await refresh();
      toast.success("Contact info saved");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="bg-white border border-[#E2DCD2] rounded-xl p-6 mb-6" data-testid="settings-contact">
      <div className="flex items-center gap-2 mb-4">
        <Phone size={18} className="text-[#2D5A47]" />
        <h3 className="font-semibold text-lg">Business Contact Info</h3>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Support mobile number" hint="Shown in footer as tel: link">
          <PhoneNumberInput
            value={f.contact_mobile}
            onChange={(value) => setF({ ...f, contact_mobile: value })}
            testId="contact-mobile"
          />
        </Field>
        <Field label="Support email" hint="Shown in footer as mailto: link">
          <input type="email" value={f.contact_email} onChange={(e) => setF({ ...f, contact_email: e.target.value })} placeholder="support@eiraproducts.com" className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="contact-email" />
        </Field>
        <Field label="Business address" hint="Multi-line supported" span="md:col-span-2">
          <textarea value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} rows={3} placeholder={"123 Main Road\nCity, State 000000\nIndia"} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="contact-address" />
        </Field>
      </div>
      <button onClick={save} disabled={saving} className="mt-4 bg-[#2D5A47] hover:bg-[#214435] disabled:opacity-50 text-white px-5 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-1.5" data-testid="save-contact">
        <Save size={14} /> {saving ? "Saving…" : "Save contact info"}
      </button>
    </div>
  );
};

const SOCIAL_META = [
  { key: "facebook", label: "Facebook", icon: Facebook, placeholder: "https://facebook.com/eiraproducts" },
  { key: "instagram", label: "Instagram", icon: Instagram, placeholder: "https://instagram.com/eiraproducts" },
  { key: "youtube", label: "YouTube", icon: Youtube, placeholder: "https://youtube.com/@eiraproducts" },
  { key: "website", label: "Website", icon: Globe, placeholder: "https://eiraproducts.com" },
];

const SocialLinksCard = () => {
  const { socials, refresh } = useBranding();
  const [rows, setRows] = useState(() => Object.fromEntries(SOCIAL_META.map((m) => [m.key, { url: "", enabled: false }])));
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (socials) {
      setRows(Object.fromEntries(SOCIAL_META.map((m) => [m.key, { url: socials[m.key]?.url || "", enabled: !!socials[m.key]?.enabled }])));
    }
  }, [socials]);
  const setRow = (key, patch) => setRows((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/branding", { socials: rows });
      await refresh();
      toast.success("Social links saved");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="bg-white border border-[#E2DCD2] rounded-xl p-6 mb-6" data-testid="settings-socials">
      <div className="flex items-center gap-2 mb-4">
        <Globe size={18} className="text-[#2D5A47]" />
        <h3 className="font-semibold text-lg">Social Links</h3>
      </div>
      <p className="text-xs text-[#8C6D46] mb-4">Paste the URL and flip the switch. Only enabled links appear in the footer.</p>
      <div className="space-y-3">
        {SOCIAL_META.map(({ key, label, icon: Icon, placeholder }) => (
          <div key={key} className="flex items-center gap-3 bg-[#F4F1EA] border border-[#E2DCD2] rounded-lg px-3 py-2" data-testid={`social-row-${key}`}>
            <Icon size={16} className="text-[#2D5A47] flex-shrink-0" />
            <div className="w-24 text-sm font-semibold text-[#1E2A24]">{label}</div>
            <input
              value={rows[key]?.url || ""}
              onChange={(e) => setRow(key, {
                url: e.target.value,
                enabled: !!e.target.value.trim(),
              })}
              placeholder={placeholder}
              className="flex-1 bg-white border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm min-w-0"
              data-testid={`social-url-${key}`}
            />
            <label className="flex items-center gap-2 text-xs cursor-pointer select-none" data-testid={`social-toggle-${key}`}>
              <input
                type="checkbox"
                checked={!!rows[key]?.enabled}
                onChange={(e) => setRow(key, { enabled: e.target.checked })}
                className="w-4 h-4 accent-[#2D5A47]"
              />
              <span className={`w-16 text-center font-semibold ${rows[key]?.enabled ? "text-emerald-700" : "text-[#8C6D46]"}`}>
                {rows[key]?.enabled ? "Enabled" : "Disabled"}
              </span>
            </label>
          </div>
        ))}
      </div>
      <button onClick={save} disabled={saving} className="mt-4 bg-[#2D5A47] hover:bg-[#214435] disabled:opacity-50 text-white px-5 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-1.5" data-testid="save-socials">
        <Save size={14} /> {saving ? "Saving…" : "Save social links"}
      </button>
    </div>
  );
};

const ContentPagesCard = () => {
  const [pages, setPages] = useState([]);
  const [active, setActive] = useState("about");
  const [draft, setDraft] = useState({ title: "", body_html: "", published: true });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/cms/pages");
    setPages(data);
    const current = data.find((page) => page.slug === "about") || data[0];
    if (current) {
      setActive(current.slug);
      setDraft({ title: current.title || "", body_html: current.body_html || "", published: !!current.published });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const select = (slug) => {
    const p = pages.find((x) => x.slug === slug);
    if (!p) return;
    setActive(slug);
    setDraft({ title: p.title || "", body_html: p.body_html || "", published: !!p.published });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/cms/pages/${active}`, draft);
      setPages((prev) => prev.map((p) => (p.slug === active ? data : p)));
      toast.success(`${data.title} saved${data.published ? "" : " (unpublished)"}`);
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-[#E2DCD2] rounded-xl p-6 mb-6" data-testid="settings-cms">
      <div className="flex items-center gap-2 mb-4">
        <FileText size={18} className="text-[#2D5A47]" />
        <h3 className="font-semibold text-lg">Content Pages</h3>
      </div>
      <p className="text-xs text-[#8C6D46] mb-4">These are the pages linked from the site footer. Toggle "Published" to hide a page without deleting its content.</p>
      <div className="grid md:grid-cols-4 gap-4">
        <div className="md:col-span-1 space-y-1" data-testid="cms-page-list">
          {pages.map((p) => (
            <button
              key={p.slug}
              onClick={() => select(p.slug)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 border ${active === p.slug ? "bg-[#EBF2EE] border-[#2D5A47]" : "bg-white border-[#E2DCD2] hover:border-[#2D5A47]"}`}
              data-testid={`cms-select-${p.slug}`}
            >
              <span className="flex-1 font-medium truncate">{p.title}</span>
              {p.published ? <Eye size={12} className="text-emerald-700" /> : <EyeOff size={12} className="text-[#8C6D46]" />}
            </button>
          ))}
          {pages.length === 0 && <div className="text-xs text-[#8C6D46] italic">Loading…</div>}
        </div>
        <div className="md:col-span-3 space-y-3">
          <Field label="Page title">
            <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="cms-title" />
          </Field>
          <Field label="Body HTML" hint="Supports basic HTML — <p>, <h2>, <ul>, <a href>, <b>, <br>. Renders exactly as written.">
            <textarea value={draft.body_html} onChange={(e) => setDraft({ ...draft, body_html: e.target.value })} rows={12} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm font-mono" data-testid="cms-body" />
          </Field>
          <label className="flex items-center gap-2 text-sm cursor-pointer bg-[#F4F1EA] border border-[#E2DCD2] rounded-lg px-3 py-2.5 w-max" data-testid="cms-published-label">
            <input
              type="checkbox"
              checked={draft.published}
              onChange={(e) => setDraft({ ...draft, published: e.target.checked })}
              className="w-4 h-4 accent-[#2D5A47]"
              data-testid="cms-published"
            />
            <span className="font-medium">{draft.published ? "Published — visible on the site" : "Unpublished — hidden from footer"}</span>
          </label>
          <div className="pt-1">
            <CopyCheckPanel
              contentType="cms_page"
              title={draft.title}
              text={draft.body_html}
              ctaText=""
              testId="cms"
            />
          </div>
          <button onClick={save} disabled={saving} className="bg-[#2D5A47] hover:bg-[#214435] disabled:opacity-50 text-white px-5 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-1.5" data-testid="cms-save">
            <Save size={14} /> {saving ? "Saving…" : "Save page"}
          </button>
        </div>
      </div>
    </div>
  );
};

const EmailTemplatesTab = () => {
  const [templates, setTemplates] = useState([]);
  const [activeKey, setActiveKey] = useState(() => (
    new URLSearchParams(window.location.search).get("template") || "otp_verification"
  ));
  const [draft, setDraft] = useState({
    subject: "",
    greeting: "",
    body_text: "",
    button_text: "",
    button_link: "",
    footer_text: "",
  });
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/email-templates");
      const nextKey = data.some((template) => template.key === activeKey)
        ? activeKey
        : data[0]?.key;
      const current = data.find((template) => template.key === nextKey);
      setTemplates(data);
      setActiveKey(nextKey || "otp_verification");
      setDraft({
        subject: current?.subject || "",
        ...(current?.editor_fields || {}),
      });
    } catch (error) {
      toast.error(formatError(error));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = templates.find((template) => template.key === activeKey);

  const selectTemplate = (template) => {
    setActiveKey(template.key);
    setDraft({
      subject: template.subject || "",
      ...(template.editor_fields || {}),
    });
    setPreview(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/email-templates/${activeKey}`, draft);
      setTemplates((previous) => previous.map((template) => (
        template.key === activeKey ? data : template
      )));
      setDraft({ subject: data.subject, ...(data.editor_fields || {}) });
      toast.success("Email template saved. New emails will use it immediately.");
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSaving(false);
    }
  };

  const showPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post(`/admin/email-templates/${activeKey}/preview`, draft);
      setPreview(data);
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setPreviewing(false);
    }
  };

  const resetTemplate = async () => {
    try {
      const { data } = await api.post(`/admin/email-templates/${activeKey}/reset`);
      setTemplates((previous) => previous.map((template) => (
        template.key === activeKey ? data : template
      )));
      setDraft({ subject: data.subject, ...(data.editor_fields || {}) });
      setPreview(null);
      toast.success("Email template reset to default");
    } catch (error) {
      toast.error(formatError(error));
    }
  };

  if (templates.length === 0) {
    return <div data-testid="email-templates-loading">Loading email templates…</div>;
  }

  return (
    <div className="max-w-6xl" data-testid="email-templates-tab">
      <h2 className="font-serif text-3xl font-semibold mb-2">Email Templates</h2>
      <p className="text-[#64748B] mb-6 text-sm" data-testid="email-templates-description">
        Edit the messages sent for customer registration and order updates. Saved changes apply
        to the next email automatically.
      </p>

      <div className="grid gap-6 lg:grid-cols-[240px_minmax(0,1fr)]">
        <div className="space-y-2" data-testid="email-template-list">
          {templates.map((template) => (
            <button
              key={template.key}
              type="button"
              onClick={() => selectTemplate(template)}
              className={`w-full border px-3 py-3 text-left text-sm transition-colors ${
                activeKey === template.key
                  ? "border-[#2D5A47] bg-[#EBF2EE] text-[#1E2A24]"
                  : "border-[#E2DCD2] bg-white hover:border-[#2D5A47]"
              }`}
              data-testid={`email-template-select-${template.key}`}
            >
              <span className="block font-semibold">{template.label}</span>
              <span className="mt-1 block text-xs text-[#64748B]">
                {template.updated_at ? "Saved template" : "Default template"}
              </span>
            </button>
          ))}
        </div>

        <div className="border border-[#E2DCD2] bg-white p-5 sm:p-6" data-testid="email-template-editor">
          <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="font-semibold text-lg" data-testid="email-template-title">
                {current?.label}
              </h3>
              <p className="mt-1 text-xs text-[#8C6D46]" data-testid="email-template-variables">
                Placeholders: {(current?.variables || []).map((variable) => `{{${variable}}}`).join(", ")}
              </p>
            </div>
            {current?.updated_at && (
              <span className="text-xs text-[#64748B]" data-testid="email-template-last-saved">
                Last saved {new Date(current.updated_at).toLocaleString("en-IN")}
              </span>
            )}
          </div>

          <div className="space-y-4">
            <Field label="Email subject">
              <input
                value={draft.subject}
                onChange={(event) => setDraft({ ...draft, subject: event.target.value })}
                className="w-full border border-[#E2DCD2] px-3 py-2 text-sm"
                data-testid="email-template-subject-input"
              />
            </Field>

            <Field label="Greeting" hint="You can use placeholders such as {{name}}.">
              <input value={draft.greeting} onChange={(event) => setDraft({ ...draft, greeting: event.target.value })} className="w-full border border-[#E2DCD2] px-3 py-2 text-sm" data-testid="email-template-greeting-input" />
            </Field>
            <Field label="Body text" hint="Write your message in plain text. Product, order, and offer details remain automatic.">
              <textarea value={draft.body_text} onChange={(event) => setDraft({ ...draft, body_text: event.target.value })} rows={7} className="w-full border border-[#E2DCD2] px-3 py-2 text-sm" data-testid="email-template-body-text-input" />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Button text" hint="Leave blank for emails without a button.">
                <input value={draft.button_text} onChange={(event) => setDraft({ ...draft, button_text: event.target.value })} className="w-full border border-[#E2DCD2] px-3 py-2 text-sm" data-testid="email-template-button-text-input" />
              </Field>
              <Field label="Button link" hint="Use an approved placeholder such as {{tracking_url}}.">
                <input value={draft.button_link} onChange={(event) => setDraft({ ...draft, button_link: event.target.value })} className="w-full border border-[#E2DCD2] px-3 py-2 text-sm" data-testid="email-template-button-link-input" />
              </Field>
            </div>
            <Field label="Footer text">
              <input value={draft.footer_text} onChange={(event) => setDraft({ ...draft, footer_text: event.target.value })} className="w-full border border-[#E2DCD2] px-3 py-2 text-sm" data-testid="email-template-footer-input" />
            </Field>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="flex items-center gap-1.5 bg-[#2D5A47] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                data-testid="email-template-save-button"
              >
                <Save size={14} />
                {saving ? "Saving…" : "Save template"}
              </button>
              <button
                type="button"
                onClick={showPreview}
                disabled={previewing}
                className="flex items-center gap-1.5 border border-[#2D5A47] px-5 py-2.5 text-sm font-semibold text-[#2D5A47] disabled:opacity-50"
                data-testid="email-template-preview-button"
              >
                <Eye size={14} />
                {previewing ? "Preparing…" : "Preview"}
              </button>
              <button
                type="button"
                onClick={resetTemplate}
                className="border border-[#D9C3A5] px-5 py-2.5 text-sm font-semibold text-[#8C6D46]"
                data-testid="email-template-reset-button"
              >
                Reset to default
              </button>
            </div>
            <CopyCheckPanel
              contentType="email_template"
              title={draft.subject}
              text={`${draft.greeting}\n${draft.body_text}\n${draft.footer_text}`}
              ctaText={draft.button_text}
              testId="email-template"
            />
          </div>

          {preview && (
            <div className="mt-8 border-t border-[#E2DCD2] pt-5" data-testid="email-template-preview-panel">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h4 className="font-semibold">Preview</h4>
                <span className="text-xs text-[#64748B]" data-testid="email-template-preview-subject">
                  Subject: {preview.subject}
                </span>
              </div>
              <iframe
                title="Email template preview"
                sandbox=""
                srcDoc={preview.body_html}
                className="h-80 w-full border border-[#E2DCD2] bg-white"
                data-testid="email-template-preview-frame"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AnalyticsTrackingCard = ({ measurementId, metaPixelId, onSaved }) => {
  const [googleValue, setGoogleValue] = useState(measurementId || "");
  const [metaValue, setMetaValue] = useState(metaPixelId || "");
  const [savingGoogle, setSavingGoogle] = useState(false);
  const [savingMeta, setSavingMeta] = useState(false);
  const normalizedGoogleValue = googleValue.trim().toUpperCase();
  const normalizedMetaValue = metaValue.trim();
  const isGoogleValid = /^G-[A-Z0-9]{7,}$/.test(normalizedGoogleValue);
  const isMetaValid = /^\d{5,32}$/.test(normalizedMetaValue);

  useEffect(() => {
    setGoogleValue(measurementId || "");
  }, [measurementId]);

  useEffect(() => {
    setMetaValue(metaPixelId || "");
  }, [metaPixelId]);

  const saveGoogle = async () => {
    if (!isGoogleValid) {
      showAdminFieldErrors("Use a Google Analytics Measurement ID such as G-ABC1234", [
        "google-analytics-measurement-id-input",
      ]);
      return;
    }
    setSavingGoogle(true);
    try {
      const { data } = await api.put("/admin/settings", {
        google_analytics_measurement_id: normalizedGoogleValue,
      });
      applyTrackingSettings(data);
      onSaved(data);
      toast.success("Google Analytics setting saved");
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSavingGoogle(false);
    }
  };

  const clearGoogle = async () => {
    setSavingGoogle(true);
    try {
      const { data } = await api.delete("/admin/settings/google-analytics");
      applyTrackingSettings(data);
      onSaved(data);
      setGoogleValue("");
      toast.success("Google Analytics setting removed");
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSavingGoogle(false);
    }
  };

  const saveMeta = async () => {
    if (!isMetaValid) {
      showAdminFieldErrors("Use a numeric Meta Pixel ID", ["meta-pixel-id-input"]);
      return;
    }
    setSavingMeta(true);
    try {
      const { data } = await api.put("/admin/settings", {
        meta_pixel_id: normalizedMetaValue,
      });
      applyTrackingSettings(data);
      onSaved(data);
      toast.success("Meta Pixel setting saved");
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSavingMeta(false);
    }
  };

  const clearMeta = async () => {
    setSavingMeta(true);
    try {
      const { data } = await api.delete("/admin/settings/meta-pixel");
      applyTrackingSettings(data);
      onSaved(data);
      setMetaValue("");
      toast.success("Meta Pixel setting removed");
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSavingMeta(false);
    }
  };

  return (
    <section
      className="mb-6 rounded-xl border border-[#E2DCD2] bg-white p-6"
      data-testid="analytics-tracking-settings"
    >
      <h3 className="text-lg font-semibold">Analytics &amp; Tracking</h3>
      <p className="mt-2 text-sm text-[#64748B]">
        Visitors can enable these services from the cookie consent banner.
      </p>
      <div className="mt-5 grid gap-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <label className="min-w-0 flex-1 text-sm font-medium text-[#1E2A24]">
          Google Analytics Measurement ID
          <input
            value={googleValue}
            onChange={(event) => setGoogleValue(event.target.value)}
            placeholder="G-ABC1234"
            autoComplete="off"
            className="mt-1 block w-full rounded-lg border border-[#E2DCD2] px-3 py-2.5 font-mono text-sm uppercase outline-none focus:ring-2 focus:ring-[#2D5A47]"
            data-testid="google-analytics-measurement-id-input"
          />
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={saveGoogle}
            disabled={savingGoogle}
            className="rounded-lg bg-[#2D5A47] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#214435] disabled:opacity-50"
            data-testid="save-google-analytics-measurement-id"
          >
            {savingGoogle ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            onClick={clearGoogle}
            disabled={savingGoogle || !measurementId}
            className="rounded-lg border border-red-200 px-4 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
            data-testid="clear-google-analytics-measurement-id"
          >
            Clear
          </button>
        </div>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <label className="min-w-0 flex-1 text-sm font-medium text-[#1E2A24]">
            Meta Pixel ID
            <input
              value={metaValue}
              onChange={(event) => setMetaValue(event.target.value)}
              placeholder="123456789012345"
              autoComplete="off"
              inputMode="numeric"
              className="mt-1 block w-full rounded-lg border border-[#E2DCD2] px-3 py-2.5 font-mono text-sm outline-none focus:ring-2 focus:ring-[#2D5A47]"
              data-testid="meta-pixel-id-input"
            />
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={saveMeta}
              disabled={savingMeta}
              className="rounded-lg bg-[#2D5A47] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#214435] disabled:opacity-50"
              data-testid="save-meta-pixel-id"
            >
              {savingMeta ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={clearMeta}
              disabled={savingMeta || !metaPixelId}
              className="rounded-lg border border-red-200 px-4 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
              data-testid="clear-meta-pixel-id"
            >
              Clear
            </button>
          </div>
        </div>
      </div>
      {measurementId && (
        <p
          className="mt-3 text-xs font-medium text-emerald-700"
          data-testid="google-analytics-active-id"
        >
          Active: {measurementId}
        </p>
      )}
      {metaPixelId && (
        <p
          className="mt-2 text-xs font-medium text-emerald-700"
          data-testid="meta-pixel-active-id"
        >
          Active: {metaPixelId}
        </p>
      )}
    </section>
  );
};

const SettingsTab = () => {
  const [s, setS] = useState(null);
  const [lowStockThreshold, setLowStockThreshold] = useState("5");
  const [savingThreshold, setSavingThreshold] = useState(false);
  const [pickupAddress, setPickupAddress] = useState({
    name: "",
    phone: "",
    line1: "",
    city: "",
    state: "",
    pincode: "",
    country: "India",
  });
  const [savingPickupAddress, setSavingPickupAddress] = useState(false);
  const [adminNotificationEmail, setAdminNotificationEmail] = useState("");
  const [savingAdminNotificationEmail, setSavingAdminNotificationEmail] = useState(false);
  const load = () => api.get("/admin/settings").then((r) => {
    setS(r.data);
    setLowStockThreshold(String(r.data.low_stock_threshold ?? 5));
    setAdminNotificationEmail(r.data.admin_order_notification_email || "");
    setPickupAddress({
      name: "",
      phone: "",
      line1: "",
      city: "",
      state: "",
      pincode: "",
      country: "India",
      ...(r.data.pickup_address || {}),
    });
  });
  useEffect(() => { load(); }, []);
  const saveLowStockThreshold = async () => {
    const numericThreshold = Number(lowStockThreshold);
    if (!Number.isInteger(numericThreshold) || numericThreshold < 0) {
      showAdminFieldErrors("Enter a whole number of zero or more units", [
        "low-stock-threshold-input",
      ]);
      return;
    }
    setSavingThreshold(true);
    try {
      const response = await api.put("/admin/settings", {
        low_stock_threshold: numericThreshold,
      });
      setS(response.data);
      setLowStockThreshold(String(response.data.low_stock_threshold));
      toast.success("Low-stock threshold saved");
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSavingThreshold(false);
    }
  };
  const setPickupField = (key, value) => {
    setPickupAddress((current) => ({ ...current, [key]: value }));
  };
  const savePickupAddress = async () => {
    const requiredFields = ["name", "phone", "line1", "city", "state", "pincode"];
    if (requiredFields.some((field) => !String(pickupAddress[field] || "").trim())) {
      const missingFields = requiredFields
        .filter((field) => !String(pickupAddress[field] || "").trim())
        .map((field) => `pickup-warehouse-${field}`);
      showAdminFieldErrors("Complete every pickup warehouse field", missingFields);
      return;
    }
    if (String(pickupAddress.phone || "").replace(/\D/g, "").length < 10) {
      showAdminFieldErrors("Enter a valid pickup phone number", ["pickup-warehouse-phone"]);
      return;
    }
    setSavingPickupAddress(true);
    try {
      const response = await api.put("/admin/settings", {
        pickup_address: {
          ...pickupAddress,
          phone: formatIndianPhone(pickupAddress.phone),
        },
      });
      setS(response.data);
      setPickupAddress(response.data.pickup_address);
      toast.success("Pickup warehouse saved");
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSavingPickupAddress(false);
    }
  };
  const saveAdminNotificationEmail = async () => {
    setSavingAdminNotificationEmail(true);
    try {
      const response = await api.put("/admin/settings", {
        admin_order_notification_email: adminNotificationEmail.trim(),
      });
      setS(response.data);
      setAdminNotificationEmail(response.data.admin_order_notification_email || "");
      toast.success("Admin order notification email saved");
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSavingAdminNotificationEmail(false);
    }
  };
  if (!s) return <div>Loading…</div>;
  return (
    <div className="max-w-3xl">
      <h2 className="font-serif text-3xl font-semibold mb-2">Site Settings</h2>
      <p className="text-[#64748B] mb-6 text-sm">Site logo, contact info, social links and content pages. Payment gateway keys have moved to the <b>Integrations</b> tab.</p>

      <BrandingCard />
      <SiteContactCard />
      <SocialLinksCard />
      <ContentPagesCard />

      <PaymentTypesCard />
      <EmailBrandingCard />
      <HomepageCollectionsCard />
      <AnalyticsTrackingCard
        measurementId={s.google_analytics_measurement_id}
        metaPixelId={s.meta_pixel_id}
        onSaved={setS}
      />

      <div className="mb-6 rounded-xl border border-[#E2DCD2] bg-white p-6" data-testid="cart-limit-settings">
        <h3 className="text-lg font-semibold">Order Quantity Rules</h3>
        <p className="mt-2 text-sm text-[#64748B]" data-testid="online-stock-rule">Online orders: any quantity up to available stock.</p>
        <p className="mt-2 text-sm text-[#64748B]" data-testid="cod-quantity-rule">Cash on Delivery: maximum 5 total units per order. Gift boxes and hampers are online-payment-only.</p>
      </div>

      <div className="mb-6 rounded-xl border border-[#E2DCD2] bg-white p-6" data-testid="admin-order-email-settings">
        <h3 className="mb-2 text-lg font-semibold">New Order Notifications</h3>
        <p className="mb-4 text-sm text-[#64748B]">
          Send a copy of every successful order, including its PDF invoice, to this email address.
        </p>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <label className="min-w-0 flex-1 text-sm font-medium text-[#1E2A24]">
            Admin notification email
            <input
              type="email"
              value={adminNotificationEmail}
              onChange={(event) => setAdminNotificationEmail(event.target.value)}
              placeholder="orders@example.com"
              className="mt-1 block w-full rounded-lg border border-[#E2DCD2] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#2D5A47]"
              data-testid="admin-order-notification-email-input"
            />
          </label>
          <button
            type="button"
            onClick={saveAdminNotificationEmail}
            disabled={savingAdminNotificationEmail}
            className="rounded-lg bg-[#2D5A47] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#214435] disabled:opacity-50"
            data-testid="save-admin-order-notification-email"
          >
            {savingAdminNotificationEmail ? "Saving…" : "Save email"}
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E2DCD2] rounded-xl p-6 mb-6">
        <h3 className="font-semibold text-lg mb-2">Inventory Alerts</h3>
        <p className="text-sm text-[#64748B] mb-4">
          Products at or below this stock level appear in Dashboard action alerts.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs font-semibold uppercase tracking-wider text-[#8C6D46]">
            Low-stock threshold (units)
            <input
              type="number"
              min="0"
              step="1"
              value={lowStockThreshold}
              onChange={(event) => setLowStockThreshold(event.target.value)}
              className="mt-1 block w-48 border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm text-[#1E2A24]"
              data-testid="low-stock-threshold-input"
            />
          </label>
          <button
            type="button"
            onClick={saveLowStockThreshold}
            disabled={savingThreshold}
            className="bg-[#2D5A47] hover:bg-[#214435] disabled:opacity-50 text-white px-4 py-2.5 rounded-lg text-sm font-semibold"
            data-testid="save-low-stock-threshold"
          >
            {savingThreshold ? "Saving…" : "Save threshold"}
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E2DCD2] rounded-xl p-6 mb-6">
        <h3 className="font-semibold text-lg mb-3">Pickup Warehouse</h3>
        <p className="mb-4 text-sm text-[#64748B]">
          This warehouse is used as the pickup location for new Shiprocket shipments.
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="pickup-warehouse-form">
          <Field label="Pickup location name" hint="Must match the location name saved in Shiprocket">
            <input
              value={pickupAddress.name || ""}
              onChange={(event) => setPickupField("name", event.target.value)}
              className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm"
              data-testid="pickup-warehouse-name"
            />
          </Field>
          <Field label="Pickup phone">
            <PhoneNumberInput
              value={pickupAddress.phone || ""}
              onChange={(value) => setPickupField("phone", value)}
              testId="pickup-warehouse-phone"
            />
          </Field>
          <Field label="Address line" span="sm:col-span-2">
            <input
              value={pickupAddress.line1 || ""}
              onChange={(event) => setPickupField("line1", event.target.value)}
              className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm"
              data-testid="pickup-warehouse-line1"
            />
          </Field>
          <Field label="City">
            <input
              value={pickupAddress.city || ""}
              onChange={(event) => setPickupField("city", event.target.value)}
              className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm"
              data-testid="pickup-warehouse-city"
            />
          </Field>
          <Field label="State">
            <input
              value={pickupAddress.state || ""}
              onChange={(event) => setPickupField("state", event.target.value)}
              className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm"
              data-testid="pickup-warehouse-state"
            />
          </Field>
          <Field label="Pincode">
            <input
              inputMode="numeric"
              value={pickupAddress.pincode || ""}
              onChange={(event) => setPickupField("pincode", event.target.value)}
              className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm"
              data-testid="pickup-warehouse-pincode"
            />
          </Field>
          <Field label="Country">
            <input
              value={pickupAddress.country || "India"}
              onChange={(event) => setPickupField("country", event.target.value)}
              className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm"
              data-testid="pickup-warehouse-country"
            />
          </Field>
        </div>
        <button
          type="button"
          onClick={savePickupAddress}
          disabled={savingPickupAddress}
          className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-[#2D5A47] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#214435] disabled:opacity-50"
          data-testid="save-pickup-warehouse"
        >
          <Save size={14} /> {savingPickupAddress ? "Saving…" : "Save pickup warehouse"}
        </button>
      </div>
    </div>
  );
};

const HomepageCollectionsCard = () => {
  const [data, setData] = useState(null);
  const [counts, setCounts] = useState({ new_arrivals_count: 8, best_sellers_count: 8 });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const response = await api.get("/admin/homepage-collections");
      setData(response.data);
      setCounts(response.data.settings);
    } catch (error) {
      toast.error(formatError(error));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const response = await api.put("/admin/homepage-collections", {
        new_arrivals_count: Number(counts.new_arrivals_count),
        best_sellers_count: Number(counts.best_sellers_count),
      });
      setData(response.data);
      setCounts(response.data.settings);
      toast.success("Homepage collection counts saved");
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="mb-6 rounded-xl border border-[#E2DCD2] bg-white p-6"
      data-testid="homepage-collections-settings"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold">Homepage Product Collections</h3>
          <p className="mt-1 text-sm text-[#64748B]">
            New Arrivals uses product creation date. Best Sellers uses completed-order totals.
          </p>
        </div>
        <span
          className="w-fit border border-[#D9C3A5] bg-[#FDFBF7] px-2 py-1 text-xs font-semibold text-[#8C6D46]"
          data-testid="homepage-collections-auto-status"
        >
          Automatic selection
        </span>
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <label className="text-sm font-medium text-[#1E2A24]">
          New Arrivals count
          <input
            type="number"
            min="1"
            max="100"
            value={counts.new_arrivals_count}
            onChange={(event) => setCounts({
              ...counts,
              new_arrivals_count: event.target.value,
            })}
            className="mt-1 block w-full border border-[#E2DCD2] px-3 py-2"
            data-testid="homepage-new-arrivals-count-input"
          />
        </label>
        <label className="text-sm font-medium text-[#1E2A24]">
          Best Sellers count
          <input
            type="number"
            min="1"
            max="100"
            value={counts.best_sellers_count}
            onChange={(event) => setCounts({
              ...counts,
              best_sellers_count: event.target.value,
            })}
            className="mt-1 block w-full border border-[#E2DCD2] px-3 py-2"
            data-testid="homepage-best-sellers-count-input"
          />
        </label>
      </div>
      <button
        type="button"
        onClick={save}
        disabled={saving}
        className="mt-4 bg-[#2D5A47] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
        data-testid="save-homepage-collection-counts"
      >
        {saving ? "Saving…" : "Save homepage counts"}
      </button>

      {!data ? (
        <p className="mt-6 text-sm text-[#64748B]" data-testid="homepage-collections-loading">
          Loading automatic collection preview…
        </p>
      ) : (
        <div className="mt-7 grid gap-6 lg:grid-cols-2" data-testid="homepage-collections-preview">
          <CollectionPreview
            title="New Arrivals"
            description="Newest active products by creation date."
            items={data.new_arrivals || []}
            testId="new-arrivals-preview"
            meta={(product) => (
              product.created_at
                ? `Added ${new Date(product.created_at).toLocaleDateString("en-IN")}`
                : "Created date unavailable"
            )}
          />
          <CollectionPreview
            title="Best Sellers"
            description="Active products with the most completed orders."
            items={data.best_sellers || []}
            testId="best-sellers-preview"
            productForItem={(entry) => entry.product}
            meta={(entry) => (
              `${entry.completed_order_count} completed orders · ${entry.units_sold} units`
            )}
          />
        </div>
      )}
    </section>
  );
};

const CollectionPreview = ({
  title,
  description,
  items,
  testId,
  productForItem = (item) => item,
  meta,
}) => (
  <div className="border border-[#E2DCD2] bg-[#FDFBF7] p-4" data-testid={testId}>
    <h4 className="font-semibold text-[#1E2A24]">{title}</h4>
    <p className="mt-1 text-xs text-[#64748B]">{description}</p>
    <div className="mt-4 divide-y divide-[#E2DCD2]">
      {items.length ? items.map((item, index) => {
        const product = productForItem(item) || {};
        return (
          <div
            key={product.id || `${testId}-${index}`}
            className="flex min-w-0 items-center gap-3 py-3 first:pt-0"
            data-testid={`${testId}-item-${product.id || index}`}
          >
            {product.images?.[0] ? (
              <img
                src={product.images[0]}
                alt=""
                className="h-10 w-10 flex-none object-cover"
                data-testid={`${testId}-image-${product.id || index}`}
              />
            ) : (
              <div
                className="h-10 w-10 flex-none bg-[#E2DCD2]"
                data-testid={`${testId}-image-placeholder-${product.id || index}`}
              />
            )}
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-[#1E2A24]">
                {product.name || "Unnamed product"}
              </p>
              <p className="mt-0.5 text-xs text-[#64748B]">{meta(item)}</p>
            </div>
          </div>
        );
      }) : (
        <p className="py-3 text-sm text-[#64748B]" data-testid={`${testId}-empty`}>
          No eligible products yet.
        </p>
      )}
    </div>
  </div>
);

const PaymentTypesCard = () => {
  const [paymentMethods, setPaymentMethods] = useState(null);
  const [savingMethod, setSavingMethod] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/settings");
    setPaymentMethods({
      razorpay_enabled: data.razorpay_enabled,
      cod_enabled: data.cod_enabled,
    });
  };

  useEffect(() => {
    load().catch((error) => toast.error(formatError(error)));
  }, []);

  const toggleMethod = async (key) => {
    if (!paymentMethods || savingMethod) return;
    const nextValue = !paymentMethods[key];
    setSavingMethod(key);
    try {
      const { data } = await api.put("/admin/settings", { [key]: nextValue });
      setPaymentMethods({
        razorpay_enabled: data.razorpay_enabled,
        cod_enabled: data.cod_enabled,
      });
      toast.success(`${key === "razorpay_enabled" ? "Razorpay" : "Cash on Delivery"} ${nextValue ? "enabled" : "disabled"}`);
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSavingMethod("");
    }
  };

  const methods = [
    {
      key: "razorpay_enabled",
      name: "Razorpay",
      detail: "UPI, cards and net banking",
      icon: CreditCard,
    },
    {
      key: "cod_enabled",
      name: "Cash on Delivery",
      detail: "Available for orders of ₹499 and above",
      icon: Truck,
    },
  ];

  return (
    <div
      className="mb-6 rounded-xl border border-[#E2DCD2] bg-white p-6"
      data-testid="settings-payment-types"
    >
      <div className="mb-4 flex items-center gap-2">
        <CreditCard size={18} className="text-[#2D5A47]" />
        <h3 className="text-lg font-semibold">Payment Types</h3>
      </div>
      <p className="mb-4 text-xs text-[#8C6D46]">
        Turn methods on or off for checkout. Disabled methods cannot be selected or submitted.
      </p>
      {!paymentMethods ? (
        <div className="text-sm text-[#64748B]" data-testid="payment-types-loading">
          Loading payment types…
        </div>
      ) : (
        <div className="space-y-3">
          {methods.map(({ key, name, detail, icon: Icon }) => {
            const enabled = !!paymentMethods[key];
            const saving = savingMethod === key;
            return (
              <div
                key={key}
                className={`flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between ${
                  enabled
                    ? "border-emerald-200 bg-emerald-50"
                    : "border-[#7F1D1D] bg-red-50"
                }`}
                data-testid={`payment-type-${key}`}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <Icon
                    size={18}
                    className={enabled ? "text-emerald-700" : "text-[#7F1D1D]"}
                  />
                  <div className="min-w-0">
                    <div className="font-semibold text-[#1E2A24]">{name}</div>
                    <div className="text-xs text-[#64748B]">{detail}</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => toggleMethod(key)}
                  disabled={savingMethod !== ""}
                  className={`min-w-28 rounded-lg px-4 py-2 text-sm font-semibold text-white transition-colors disabled:cursor-wait disabled:opacity-60 ${
                    enabled
                      ? "bg-emerald-600 hover:bg-emerald-700"
                      : "bg-[#7F1D1D] hover:bg-[#641515]"
                  }`}
                  data-testid={`toggle-payment-${key}`}
                  aria-pressed={enabled}
                >
                  {saving ? "Saving…" : enabled ? "Enabled" : "Disabled"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const EmailBrandingCard = () => {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/email-branding").then((response) => setConfig(response.data));
  }, []);

  if (!config) {
    return null;
  }

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/email-branding", config);
      setConfig(data);
      toast.success("Email branding saved");
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-6 border border-[#E2DCD2] bg-white p-6" data-testid="email-branding-settings">
      <h3 className="text-lg font-semibold">Email Branding</h3>
      <p className="mt-2 text-sm text-[#64748B]">
        The site logo appears in every email. Set where customers go when they click it.
      </p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="text-sm font-medium">
          Email display name
          <input
            value={config.display_name}
            onChange={(event) => setConfig({ ...config, display_name: event.target.value })}
            className="mt-1 block w-full border border-[#E2DCD2] px-3 py-2"
            data-testid="email-brand-name"
          />
        </label>
        <label className="text-sm font-medium">
          Link your URL
          <input
            type="url"
            value={config.logo_click_url || ""}
            onChange={(event) => setConfig({ ...config, logo_click_url: event.target.value })}
            placeholder="https://eiraproducts.com"
            className="mt-1 block w-full border border-[#E2DCD2] px-3 py-2"
            data-testid="email-logo-click-url"
          />
          <span className="mt-1 block text-xs font-normal text-[#64748B]">
            Leave blank to open the Eira home page.
          </span>
        </label>
        <label className="text-sm font-medium">
          Logo width (px)
          <input
            type="number"
            value={config.logo_width}
            onChange={(event) => setConfig({ ...config, logo_width: Number(event.target.value) })}
            className="mt-1 block w-full border border-[#E2DCD2] px-3 py-2"
            data-testid="email-logo-width"
          />
        </label>
        <label className="text-sm font-medium">
          Logo height (px)
          <input
            type="number"
            value={config.logo_height}
            onChange={(event) => setConfig({ ...config, logo_height: Number(event.target.value) })}
            className="mt-1 block w-full border border-[#E2DCD2] px-3 py-2"
            data-testid="email-logo-height"
          />
        </label>
      </div>
      <div className="mt-5 border border-[#E2DCD2] bg-[#FDFBF7] p-4" data-testid="email-brand-preview">
        {config.site_logo_url ? (
          <a
            href={config.logo_click_url || "https://eiraproducts.com"}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="email-brand-preview-logo-link"
          >
            <img
              src={config.site_logo_url}
              alt={config.display_name}
              style={{
                width: config.logo_width,
                height: config.logo_height,
                objectFit: "contain",
              }}
              data-testid="email-brand-preview-logo"
            />
          </a>
        ) : (
          <div className="font-serif text-xl text-[#2D5A47]">{config.display_name}</div>
        )}
      </div>
      <button
        type="button"
        onClick={save}
        disabled={saving}
        className="mt-5 bg-[#2D5A47] px-5 py-2.5 text-sm font-semibold text-white"
        data-testid="save-email-branding"
      >
        {saving ? "Saving…" : "Save email branding"}
      </button>
    </div>
  );
};

const IntegrationsTab = () => {
  const [s, setS] = useState(null);
  const [f, setF] = useState({ shiprocket_email: "", shiprocket_password: "", razorpay_key_id: "", razorpay_key_secret: "", razorpay_webhook_secret: "" });
  const [open, setOpen] = useState({ razorpay: false, shiprocket: false });
  const [saving, setSaving] = useState(false);
  const [savingShiprocketMode, setSavingShiprocketMode] = useState(false);
  const [disconnectTarget, setDisconnectTarget] = useState(null);
  const load = () => api.get("/admin/settings").then((r) => setS(r.data));
  useEffect(() => { load(); }, []);
  const saveProvider = async (provider) => {
    const keys = provider === "razorpay"
      ? ["razorpay_key_id", "razorpay_key_secret", "razorpay_webhook_secret"]
      : ["shiprocket_email", "shiprocket_password"];
    const payload = Object.fromEntries(keys.filter((key) => f[key]?.trim()).map((key) => [key, f[key].trim()]));
    const required = provider === "razorpay"
      ? ["razorpay_key_id", "razorpay_key_secret"]
      : ["shiprocket_email", "shiprocket_password"];
    if (required.some((key) => !f[key]?.trim())) {
      const missingFields = required.filter((key) => !f[key]?.trim()).map((key) => ({
        razorpay_key_id: "rzp-key",
        razorpay_key_secret: "rzp-secret",
        shiprocket_email: "sr-email",
        shiprocket_password: "sr-password",
      }[key]));
      showAdminFieldErrors("Enter the required credentials", missingFields);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/integrations/${provider}/connect`, payload);
      setS(data);
      setOpen({ ...open, [provider]: false });
      setF({
        shiprocket_email: "",
        shiprocket_password: "",
        razorpay_key_id: "",
        razorpay_key_secret: "",
        razorpay_webhook_secret: "",
      });
      toast.success(`${provider === "razorpay" ? "Razorpay" : "Shiprocket"} connected`);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };
  const saveRazorpayWebhookSecret = async () => {
    const webhookSecret = f.razorpay_webhook_secret.trim();
    if (!webhookSecret) {
      showAdminFieldErrors("Enter the Razorpay webhook secret", ["rzp-webhook"]);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", {
        razorpay_webhook_secret: webhookSecret,
      });
      setS(data);
      setF({ ...f, razorpay_webhook_secret: "" });
      toast.success("Razorpay webhook secret saved");
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSaving(false);
    }
  };
  const disconnectProvider = async () => {
    if (!disconnectTarget) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/integrations/${disconnectTarget}/disconnect`);
      setS(data);
      setOpen({ ...open, [disconnectTarget]: false });
      toast.success(`${disconnectTarget === "razorpay" ? "Razorpay" : "Shiprocket"} disconnected`);
      setDisconnectTarget(null);
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSaving(false);
    }
  };
  const toggleShiprocketLiveOperations = async () => {
    const nextValue = !s.shiprocket_live_operations_requested;
    setSavingShiprocketMode(true);
    try {
      const { data } = await api.put("/admin/settings", {
        shiprocket_live_operations_enabled: nextValue,
      });
      setS(data);
      toast.success(nextValue ? "Shiprocket live operations enabled" : "Shiprocket mock mode enabled");
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSavingShiprocketMode(false);
    }
  };
  if (!s) return <div>Loading…</div>;
  const Status = ({ ok, label }) => (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
      {ok ? <CheckCircle2 size={12} /> : <XCircle size={12} />} {label}
    </span>
  );
  return (
    <div className="max-w-3xl">
      <h2 className="font-serif text-3xl font-semibold mb-2">Integrations</h2>
      <p className="text-[#64748B] mb-6 text-sm">Paste your production keys below. They're stored in the database and take effect on the very next order. Values are never sent back to the browser — only status flags.</p>

      <div className="bg-white border border-[#E2DCD2] rounded-xl p-6 mb-6" data-testid="integration-razorpay">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <CreditCard size={18} className="text-[#2D5A47]" />
            <h3 className="font-semibold text-lg">Razorpay</h3>
          </div>
          <div className="flex items-center gap-2"><Status ok={s.razorpay_configured} label={s.razorpay_configured ? "Connected" : "Disconnected"} /><button type="button" onClick={() => s.razorpay_configured ? setDisconnectTarget("razorpay") : setOpen({ ...open, razorpay: !open.razorpay })} className="rounded border border-[#2D5A47] px-3 py-1 text-xs font-semibold text-[#2D5A47]" data-testid="connect-razorpay">{s.razorpay_configured ? "Disconnect" : "Connect"}</button></div>
        </div>
        {s.razorpay_configured && s.razorpay_key_id_masked && (
          <div className="text-xs text-[#64748B] mb-3">Current key: <span className="font-mono">{s.razorpay_key_id_masked}</span></div>
        )}
        {open.razorpay && <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Key ID" hint="Starts with rzp_live_ or rzp_test_">
              <input
                placeholder="rzp_live_XXXXXXXXXXXX"
                value={f.razorpay_key_id}
                onChange={(e) => setF({ ...f, razorpay_key_id: e.target.value })}
                className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm font-mono"
                data-testid="rzp-key"
              />
            </Field>
            <Field label="Key Secret" hint="Hidden after save">
              <input
                type="password"
                placeholder="••••••••••••••••"
                value={f.razorpay_key_secret}
                onChange={(e) => setF({ ...f, razorpay_key_secret: e.target.value })}
                className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm"
                data-testid="rzp-secret"
              />
            </Field>
            <Field
              label="Webhook Secret"
              hint="Matches the Razorpay Dashboard webhook secret"
              span="sm:col-span-2"
            >
              <input
                type="password"
                placeholder="••••••••••••••••"
                value={f.razorpay_webhook_secret}
                onChange={(e) => setF({ ...f, razorpay_webhook_secret: e.target.value })}
                className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm"
                data-testid="rzp-webhook"
              />
            </Field>
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              onClick={() => saveProvider("razorpay")}
              disabled={saving}
              className="rounded bg-[#2D5A47] px-4 py-2 text-sm font-semibold text-white"
              data-testid="save-razorpay"
            >
              Connect Razorpay
            </button>
            <button
              onClick={saveRazorpayWebhookSecret}
              disabled={saving}
              className="rounded border border-[#2D5A47] px-4 py-2 text-sm font-semibold text-[#2D5A47]"
              data-testid="save-razorpay-webhook-secret"
            >
              Save webhook secret
            </button>
          </div>
        </>}
        <div className="text-xs text-[#8C6D46] mt-3">
          Get keys at <span className="font-semibold">dashboard.razorpay.com → Settings → API Keys</span>. Webhook secret is under <b>Settings → Webhooks</b>.
        </div>
      </div>

      <div className="bg-white border border-[#E2DCD2] rounded-xl p-6 mb-6" data-testid="integration-shiprocket">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Truck size={18} className="text-[#2D5A47]" />
            <h3 className="font-semibold text-lg">Shiprocket</h3>
          </div>
          <div className="flex items-center gap-2"><Status ok={s.shiprocket_configured} label={s.shiprocket_configured ? "Connected" : "Disconnected"} /><button type="button" onClick={() => s.shiprocket_configured ? setDisconnectTarget("shiprocket") : setOpen({ ...open, shiprocket: !open.shiprocket })} className="rounded border border-[#2D5A47] px-3 py-1 text-xs font-semibold text-[#2D5A47]" data-testid="connect-shiprocket">{s.shiprocket_configured ? "Disconnect" : "Connect"}</button></div>
        </div>
        {s.shiprocket_configured && s.shiprocket_email_masked && (
          <div className="text-xs text-[#64748B] mb-3">Current account: <span className="font-mono">{s.shiprocket_email_masked}</span></div>
        )}
        <div
          className="mb-4 border border-[#E2DCD2] bg-[#FDFBF7] p-4"
          data-testid="shiprocket-live-operations-panel"
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="font-semibold text-[#1E2A24]">Shiprocket live operations</div>
              <p className="mt-1 text-sm text-[#64748B]">
                {s.shiprocket_runtime_mode === "live"
                  ? "New eligible orders use the live Shiprocket path."
                  : "New orders remain in mock mode until live operations are enabled."}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Status
                ok={s.shiprocket_runtime_mode === "live"}
                label={s.shiprocket_runtime_mode === "live" ? "Live" : "Mock"}
              />
              <button
                type="button"
                onClick={toggleShiprocketLiveOperations}
                disabled={savingShiprocketMode || (
                  !s.shiprocket_configured && !s.shiprocket_live_operations_requested
                )}
                className="rounded border border-[#2D5A47] px-3 py-2 text-xs font-semibold text-[#2D5A47] disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="toggle-shiprocket-live-operations"
                aria-pressed={s.shiprocket_live_operations_enabled}
              >
                {savingShiprocketMode
                  ? "Saving…"
                  : s.shiprocket_live_operations_requested
                    ? "Switch to mock"
                    : "Enable live"}
              </button>
            </div>
          </div>
          {!s.shiprocket_validated && (
            <p className="mt-3 text-xs font-semibold text-red-700" data-testid="shiprocket-validation-warning">
              Connect and validate Shiprocket credentials before enabling live operations.
            </p>
          )}
        </div>
        {open.shiprocket && <><div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Account email">
            <input type="email" placeholder="you@company.com" value={f.shiprocket_email} onChange={(e) => setF({ ...f, shiprocket_email: e.target.value })} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="sr-email" />
          </Field>
          <Field label="Account password" hint="Hidden after save">
            <input type="password" placeholder="••••••••••••••••" value={f.shiprocket_password} onChange={(e) => setF({ ...f, shiprocket_password: e.target.value })} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="sr-password" />
          </Field>
        </div><button onClick={() => saveProvider("shiprocket")} disabled={saving} className="mt-4 rounded bg-[#2D5A47] px-4 py-2 text-sm font-semibold text-white" data-testid="save-shiprocket">Connect Shiprocket</button></>}
        <div className="text-xs text-[#8C6D46] mt-3">
          Get credentials at <span className="font-semibold">dashboard.shiprocket.in → Settings → API → Users</span>. Once saved, new orders will auto-generate real AWBs.
        </div>
      </div>

      {disconnectTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#1E2A24]/55 p-4" data-testid="disconnect-integration-modal">
          <div className="w-full max-w-sm bg-white p-6 shadow-xl">
            <h3 className="font-serif text-xl font-semibold text-[#1E2A24]">
              Disconnect {disconnectTarget === "razorpay" ? "Razorpay" : "Shiprocket"}?
            </h3>
            <p className="mt-2 text-sm text-[#64748B]">
              New orders will no longer use this integration until you connect it again.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setDisconnectTarget(null)} disabled={saving} className="px-4 py-2 text-sm font-semibold text-[#64748B]" data-testid="cancel-disconnect-integration">Cancel</button>
              <button type="button" onClick={disconnectProvider} disabled={saving} className="bg-red-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="confirm-disconnect-integration">{saving ? "Disconnecting…" : "Disconnect"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PAPER_SIZES = [
  { v: "A4", label: "A4 (standard invoice)" },
  { v: "thermal", label: "Thermal 80 mm (POS printer)" },
];

const PrintTab = () => {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/print-settings").then((r) => setF(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/print-settings", f);
      setF(data);
      toast.success("Print format saved");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };
  if (!f) return <div>Loading…</div>;
  const set = (k, v) => setF({ ...f, [k]: v });
  return (
    <div className="max-w-4xl">
      <h2 className="font-serif text-3xl font-semibold mb-2">Print Format</h2>
      <p className="text-[#64748B] mb-6 text-sm">Customise how your invoices look when an admin hits Print on any order. Save to preview from Orders → Print.</p>
      <div className="bg-white border border-[#E2DCD2] rounded-xl p-6 mb-6" data-testid="print-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Invoice number prefix" hint="Prepended to the order ID e.g. EIRA-A1B2C3D4">
            <input value={f.invoice_prefix} onChange={(e) => set("invoice_prefix", e.target.value)} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm font-mono" data-testid="print-invoice-prefix" />
          </Field>
          <Field label="Paper size">
            <select value={f.paper_size} onChange={(e) => set("paper_size", e.target.value)} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="print-paper-size">
              {PAPER_SIZES.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
            </select>
          </Field>
          <Field label="Brand accent colour" hint="Used for header bar and total row">
            <input type="color" value={f.brand_color} onChange={(e) => set("brand_color", e.target.value)} className="border border-[#E2DCD2] rounded-lg h-10 w-full" data-testid="print-brand-color" />
          </Field>
          <Field label="GSTIN (shown when GST is enabled)">
            <input value={f.gst_number} onChange={(e) => set("gst_number", e.target.value)} placeholder="22AAAAA0000A1Z5" className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm font-mono" data-testid="print-gst-number" />
          </Field>
          <Field label="Header note" hint="Small line at the top of the invoice" span="md:col-span-2">
            <input value={f.header_note} onChange={(e) => set("header_note", e.target.value)} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="print-header-note" />
          </Field>
          <Field label="Footer note" hint="Small line above the signature" span="md:col-span-2">
            <input value={f.footer_note} onChange={(e) => set("footer_note", e.target.value)} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="print-footer-note" />
          </Field>
          <Field label="Terms & Conditions" hint="Rendered as-is below the totals" span="md:col-span-2">
            <textarea rows={3} value={f.terms} onChange={(e) => set("terms", e.target.value)} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="print-terms" />
          </Field>
          <Field label="Signature line label">
            <input value={f.signature_line} onChange={(e) => set("signature_line", e.target.value)} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="print-signature" />
          </Field>
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          {[
            { k: "show_logo", label: "Show logo" },
            { k: "show_gst", label: "Show GST line" },
            { k: "show_hsn", label: "Show HSN column" },
          ].map(({ k, label }) => (
            <label key={k} className="flex items-center gap-2 text-sm bg-[#F4F1EA] border border-[#E2DCD2] rounded-lg px-3 py-2 cursor-pointer" data-testid={`print-toggle-${k}`}>
              <input type="checkbox" checked={!!f[k]} onChange={(e) => set(k, e.target.checked)} className="w-4 h-4 accent-[#2D5A47]" />
              <span className="font-medium">{label}</span>
            </label>
          ))}
        </div>
        <button onClick={save} disabled={saving} className="mt-6 bg-[#2D5A47] hover:bg-[#214435] disabled:opacity-50 text-white px-6 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-1.5" data-testid="save-print-settings">
          <Save size={14} /> {saving ? "Saving…" : "Save print format"}
        </button>
      </div>
    </div>
  );
};

const BrandsTab = () => {
  const { requestDelete } = useAdminDeleteConfirmation();
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ slug: "", name: "", tagline: "", description: "", image: "", accent_color: "#2D5A47", order: 0, active: true });
  const [editingId, setEditingId] = useState(null);
  const load = () => api.get("/admin/brands").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { window.scrollTo({ top: 0, left: 0 }); }, [editingId]);
  const reset = () => { window.scrollTo({ top: 0, left: 0 }); setEditingId(null); setF({ slug: "", name: "", tagline: "", description: "", image: "", accent_color: "#2D5A47", order: 0, active: true }); };
  const save = async () => {
    const missingFields = [
      !f.name.trim() && "brand-name",
      !f.slug.trim() && "brand-slug-input",
    ].filter(Boolean);
    if (missingFields.length) {
      showAdminFieldErrors("Brand name and URL slug are required", missingFields);
      return;
    }
    try {
      await (editingId ? api.put(`/brands/${editingId}`, f) : api.post("/brands", f));
      toast.success(editingId ? "Brand updated" : "Brand created");
      reset();
      load();
    }
    catch (e) { toast.error(formatError(e)); }
  };
  const edit = (brand) => { window.scrollTo({ top: 0, left: 0 }); setEditingId(brand.id); setF({ ...brand }); };
  const toggle = async (brand) => { await api.put(`/brands/${brand.id}`, { ...brand, active: !brand.active }); load(); };
  const del = (id) => requestDelete(async () => {
    await api.delete(`/brands/${id}`);
    await load();
  });
  return (
    <div>
      <h2 className="font-serif text-3xl font-semibold mb-6">Brands</h2>
      <div className="bg-white border border-[#E2DCD2] rounded-xl p-5 mb-6">
        <h3 className="font-semibold mb-4 text-lg">{editingId ? "Edit Brand" : "Add Brand"}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Brand name">
            <input placeholder="e.g. Vegiva" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value, slug: f.slug || e.target.value.toLowerCase().replace(/\s+/g, "-") })} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" data-testid="brand-name" />
          </Field>
          <Field label="URL slug" hint="Auto-generated from the name">
            <input placeholder="vegiva" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm font-mono" data-testid="brand-slug-input" />
          </Field>
          <Field label="Tagline" span="md:col-span-2">
            <input placeholder="Short brand line, shown under the name" value={f.tagline} onChange={(e) => setF({ ...f, tagline: e.target.value })} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" />
          </Field>
          <Field label="Description" span="md:col-span-2">
            <textarea placeholder="1–2 sentence brand story" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="w-full border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm" rows={2} />
          </Field>
          <div className="md:col-span-2">
            <MediaPicker
              images={f.image ? [f.image] : []}
              setImages={(images) => setF({ ...f, image: images[0] || "" })}
              maxImages={1}
              allowVideo={false}
              imageHint="1600 × 900 px · JPG/PNG/WebP · ≤ 8 MB"
              testId="brand-image-upload"
            />
          </div>
          <Field label="Accent colour">
            <input type="color" value={f.accent_color} onChange={(e) => setF({ ...f, accent_color: e.target.value })} className="border border-[#E2DCD2] rounded-lg h-10 w-full" />
          </Field>
          <div className="md:col-span-2 flex gap-3">
            <button onClick={save} className="bg-[#2D5A47] hover:bg-[#214435] text-white rounded-lg px-5 py-2.5 text-sm font-semibold" data-testid="save-brand">{editingId ? "Save Brand" : "Add Brand"}</button>
            {editingId && <button onClick={reset} className="text-sm text-[#64748B]" data-testid="cancel-brand-edit">Cancel</button>}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {items.map((b) => (
          <div key={b.id} className="bg-white border border-[#E2DCD2] rounded-xl overflow-hidden">
            <div className="h-32 relative" style={{ background: b.accent_color }}>
              {b.image && <img src={b.image} alt="" className="w-full h-full object-cover opacity-60" />}
              <div className="absolute inset-0 flex items-end p-3">
                <div className="text-white">
                  <div className="font-serif text-xl">{b.name}</div>
                  <div className="text-[10px] tracking-widest uppercase">{b.tagline}</div>
                </div>
              </div>
            </div>
            <div className="p-3 flex items-center justify-between">
              <a href={`/brand/${b.slug}`} target="_blank" rel="noreferrer" className="text-xs text-[#C86D42] font-semibold">View →</a>
              <div className="flex items-center gap-1"><button onClick={() => edit(b)} className="p-1 text-[#2D5A47]" data-testid={`edit-brand-${b.id}`}><Pencil size={14} /></button><button onClick={() => toggle(b)} className={`rounded p-1 text-white ${b.active ? "bg-emerald-600" : "bg-[#7F1D1D]"}`} data-testid={`toggle-brand-${b.id}`} title={b.active ? "Disable" : "Enable"}><Power size={14} /></button><button onClick={() => del(b.id)} className="p-1 text-red-500" data-testid={`delete-brand-${b.id}`}><Trash2 size={14} /></button></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Admin = () => {
  const { user, checking, logout, updateCurrentUser } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState(getAdminTabFromUrl);
  const [dashboardOrderFilter, setDashboardOrderFilter] = useState(null);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [logoutBusy, setLogoutBusy] = useState(false);
  const requestedEmailTemplate = new URLSearchParams(window.location.search).get("template");
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [tab]);
  const handleLogout = async () => {
    setLogoutBusy(true);
    try {
      await logout();
      window.location.assign(getMainLoginUrl());
    } finally {
      setLogoutBusy(false);
      setLogoutOpen(false);
    }
  };
  const openDashboardOrders = (filter = {}) => {
    setDashboardOrderFilter(filter);
    selectTab("orders");
  };
  const selectTab = (nextTab, templateKey = "") => {
    if (!ADMIN_TABS.some((item) => item.k === nextTab)) return;
    if (nextTab !== "orders") setDashboardOrderFilter(null);
    const url = new URL(window.location.href);
    url.searchParams.set("tab", nextTab);
    if (templateKey) {
      url.searchParams.set("template", templateKey);
    } else if (nextTab !== "email-templates") {
      url.searchParams.delete("template");
    }
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    setTab(nextTab);
  };
  useEffect(() => {
    if (checking) return;
    if (!user || user.role !== "admin") nav("/login");
  }, [user, checking, nav]);
  if (!user || user.role !== "admin") return null;
  return (
    <AdminDeleteConfirmProvider>
      <div className="flex min-h-screen flex-col bg-[#FDFBF7] sm:h-screen sm:overflow-hidden sm:flex-row" data-testid="admin-page">
      <AdminSidebar
        tab={tab}
        setTab={selectTab}
        onLogout={() => setLogoutOpen(true)}
        user={user}
      />
      <div
        className="flex items-center gap-3 border-b border-[#E2DCD2] bg-white px-4 py-3 sm:hidden"
        data-testid="admin-mobile-navigation"
      >
        <select
          value={tab}
          onChange={(event) => selectTab(event.target.value)}
          className="min-w-0 flex-1 rounded-lg border border-[#E2DCD2] bg-white px-3 py-2 text-sm font-semibold text-[#1E2A24]"
          data-testid="admin-mobile-tab-selector"
        >
          {ADMIN_TABS.map((item) => (
            <option key={item.k} value={item.k}>{item.t}</option>
          ))}
        </select>
        <a
          href="https://eiraproducts.com"
          target="_blank"
          rel="noopener noreferrer"
          className={[
            "inline-flex items-center gap-1 whitespace-nowrap border border-[#2D5A47]",
            "px-2.5 py-2 text-xs font-semibold text-[#2D5A47]",
          ].join(" ")}
          data-testid="admin-mobile-back-to-store-link"
        >
          ← Back to Store
        </a>
        <button
          type="button"
          onClick={() => setLogoutOpen(true)}
          className="rounded-lg border border-[#7F1D1D] p-2 text-[#7F1D1D]"
          aria-label="Log out"
          data-testid="admin-mobile-logout-button"
        >
          <LogOut size={16} />
        </button>
      </div>
      <main
        className={`w-full min-w-0 sm:h-screen sm:flex-1 sm:overflow-y-auto ${tab === "dash" ? "p-0" : "p-4 sm:p-8"}`}
        onChangeCapture={clearAdminValidationError}
        onInputCapture={clearAdminValidationError}
        onInvalidCapture={handleAdminNativeInvalid}
      >
        {tab === "dash" && (
          <AdminDashboard
            onNavigate={selectTab}
            onOpenOrders={openDashboardOrders}
          />
        )}
        {tab === "my-profile" && <AdminProfileTab user={user} onUserUpdated={updateCurrentUser} />}
        {tab === "products" && <ProductsTab />}
        {tab === "categories" && <CategoriesTab />}
        {tab === "brands" && <BrandsTab />}
        {tab === "banners" && <BannersTab />}
        {tab === "campaigns" && <CampaignsTab />}
          {tab === "coupons" && <CouponsTab onNavigate={selectTab} />}
        {tab === "orders" && (
          <OrdersTab
            dashboardFilter={dashboardOrderFilter}
            onClearDashboardFilter={() => setDashboardOrderFilter(null)}
          />
        )}
        {tab === "returns" && <ReturnsTab />}
        {tab === "purchases" && <PurchaseManagementTab />}
        {tab === "customers" && <CustomersTab />}
        {tab === "account-deletions" && <AccountDeletionRequestsTab />}
        {tab === "eira-points" && <EiraPointsAdminTab />}
        {tab === "reviews" && <FeedbackReviewsTab />}
        {tab === "support-tickets" && <SupportTicketsTab />}
        {tab === "corporate-enquiries" && <CorporateEnquiriesTab />}
        {tab === "email-templates" && (
          requestedEmailTemplate ? (
            <EmailTemplatesTab />
          ) : (
            <>
              <EmailAutomationSettings />
              <EmailTemplatesTab />
            </>
          )
        )}
        {tab === "email-delivery" && <EmailDeliveryMonitor />}
        {tab === "integrations" && <IntegrationsTab />}
        {tab === "print" && <PrintTab />}
        {tab === "settings" && <SettingsTab />}
      </main>
      <LogoutConfirmDialog
        open={logoutOpen}
        busy={logoutBusy}
        onCancel={() => setLogoutOpen(false)}
        onConfirm={handleLogout}
        testIdPrefix="admin"
      />
      </div>
    </AdminDeleteConfirmProvider>
  );
};

export default Admin;
